(ns ductile.ui.schema-based-editor
  (:require [clojure.edn :as edn]
            [clojure.string :as str]
            [ductile.schema :as schema]
            [ductile.time.format :as time-format]
            [ductile.ui.code]
            [ductile.ui.components :as ui]
            [ductile.ui.components.combobox :as combobox]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.fahrzeug :as fahrzeug]
            [ductile.ui.http :as http]
            [ductile.ui.locale :as locale :refer [tr trn]]
            [ductile.ui.util :as ui-util]
            [ductile.ui.vehicle-call-off :refer [item-view]]
            [ductile.workflow.form-recorder :as rec]
            [lambdaisland.deep-diff2 :as ddiff]
            [lambdaisland.deep-diff2.diff-impl :as diff-impl]
            [malli.core :as m]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.viewer :as clerk-viewer]))

(declare editor'
         resolved-schema
         structural-child
         or-matching-index
         decatenate-exact
         add-button)

(def ^:export _ "Include dynamic Tailwind classes in the bundle"
  ["group/0 group-hover/0:visible"
   "group/1 group-hover/1:visible"
   "group/2 group-hover/2:visible"
   "group/3 group-hover/3:visible"
   "group/4 group-hover/4:visible"
   "group/5 group-hover/5:visible"
   "group/6 group-hover/6:visible"
   "group/7 group-hover/7:visible"
   "group/8 group-hover/8:visible"
   "group/9 group-hover/9:visible"
   "group/10 group-hover/10:visible"
   "group/11 group-hover/11:visible"
   "group/12 group-hover/12:visible"
   "group/13 group-hover/13:visible"
   "group/14 group-hover/14:visible"
   "group/15 group-hover/15:visible"
   "group/16 group-hover/16:visible"
   "group/17 group-hover/17:visible"
   "group/18 group-hover/18:visible"
   "group/19 group-hover/19:visible"
   "group/map-0 group-hover/map-0:visible"
   "group/map-1 group-hover/map-1:visible"
   "group/map-2 group-hover/map-2:visible"
   "group/map-3 group-hover/map-3:visible"
   "group/map-4 group-hover/map-4:visible"
   "group/map-5 group-hover/map-5:visible"
   "group/map-6 group-hover/map-6:visible"
   "group/map-7 group-hover/map-7:visible"
   "group/map-8 group-hover/map-8:visible"
   "group/map-9 group-hover/map-9:visible"
   "group/map-10 group-hover/map-10:visible"
   "group/map-11 group-hover/map-11:visible"
   "group/map-12 group-hover/map-12:visible"
   "group/map-13 group-hover/map-13:visible"
   "group/map-14 group-hover/map-14:visible"
   "group/map-15 group-hover/map-15:visible"
   "group/map-16 group-hover/map-16:visible"
   "group/map-17 group-hover/map-17:visible"
   "group/map-18 group-hover/map-18:visible"
   "group/map-19 group-hover/map-19:visible"])

(def schemas
  {:ductile/address [:map
                     [:address/street :string]
                     [:address/postcode [:re #"[0-9]{7}"]]
                     [:address/city :string]
                     [:address/province-code (into [:enum] schema/italian-provinces)]
                     [:address/region (into [:enum] schema/italian-regions)]]
   :equipment/item :string
   :vehicle/equipment [:vector :equipment/item]
   :ductile/addresses [:vector :ductile/address]
   :billing/pred (into [:enum] schema/price-pred-symbols)
   :service/code (into [:enum] schema/service-codes)
   :contract/name :string
   :price/amount [:int {:min 0}]
   :service/price :price/amount
   :billing/price :price/amount
   :billing/description :string
   :billing/preds [:vector :billing/pred]
   :service/dynamic-price [:map :billing/description :billing/price :billing/preds]
   :billing-template/service [:map
                              :service/code
                              [:service/price {:optional true} :price/amount]
                              [:service/prices {:optional true} [:sequential :service/dynamic-price]]]
   :billing-template/services [:sequential :billing-template/service]
   :billing/template [:map
                      :contract/name
                      :billing-template/services]
   :workflow/service schema/service-definition-schema
   :compound/name (into [:enum] schema/compound-names)
   :ductile/contract [:map
                      :contract/name
                      [:contract/compounds [:set :compound/name]]
                      [:fahrzeug/attributes [:map-of :keyword :any]]
                      [:contract/services [:vector :workflow/service]]
                      [:contract/extra-services [:vector :workflow/service]]]})

(doseq [[k schema] schemas]
  (schema/register! k schema))

(def form->type {:ductile/pred :code
                 :price/amount :double})

;; maps malli schema types to their editor view and collapse behavior
;; :view - the editor view to use (case lookup in editor component)
;; :count-by - when collapsible, how to count entries (:value or :schema)
(def schema-type-defs
  (into {}
        (for [[types def]
              [[#{:vector :sequential 'list? 'vector? 'coll? 'seqable? 'sequential?}
                {:view :list :count-by :value}]
               [#{:set 'set?}
                {:view :set :count-by :value}]
               [#{:map}
                {:view :map :count-by :schema}]
               [#{:map-of 'map?}
                {:view :map-of :count-by :value}]
               [#{:cat}
                {:view :cat :count-by :value}]
               [#{:boolean 'boolean?}
                {:view :boolean}]
               [#{:double 'double? :factor 'number?}
                {:view :double}]
               [#{:int 'int?}
                {:view :int}]
               [#{:string :string+}
                {:view :string}]]
              t types]
          [t def])))

(def simple #{:enum :string :string+ :re :keyword :boolean 'boolean? :inst :uuid :int 'int? :double 'double? 'number?
              :billing/price :factor :time/year-month})

;; Editor-internal state stored as cljs metadata on map values.
;; ::id       - stable identity for React keys across add/remove/reorder
;; ::or-index - which :or branch was selected (avoids ambiguous schema matching)

(defn assign-id
  "Attach a ::id in metadata to a map value if it doesn't have one."
  [v]
  (if (and (map? v) (not (record? v)) (not (::id (meta v))))
    (vary-meta v assoc ::id (random-uuid))
    v))

(defn mismatch? [v] (instance? diff-impl/Mismatch v))
(defn insertion? [v] (instance? diff-impl/Insertion v))
(defn deletion? [v] (instance? diff-impl/Deletion v))
(defn diff-record? [v] (or (mismatch? v) (insertion? v) (deletion? v)))

(defn diff-record-old-value [value]
  (cond
    (mismatch? value) (:- value)
    (deletion? value) (:- value)
    (insertion? value) nil
    :else value))

(defn diff-record-new-value [value]
  (cond
    (mismatch? value) (:+ value)
    (insertion? value) (:+ value)
    (deletion? value) nil
    :else value))

(defn- coerce-validation-input*
  [v]
  (cond
    ;; semi hack against transit type insconsistency WfFn <-> Clerk.RenderFn
    (clerk-viewer/render-fn? v)
    (rec/map->WorkflowFn {:form (:form v) :f nil})
    (record? v) v
    (map? v) (update-vals v coerce-validation-input*)
    (sequential? v) (mapv coerce-validation-input* v)
    (set? v) (into #{} (map coerce-validation-input*) v)
    :else v))

(defn coerce-validation-input
  "Normalize a value for validation: replace RenderFn records,
   and coerce JSON-like types (e.g. vecs to sets) via schema."
  [schema v]
  (schema/json-coerce schema (coerce-validation-input* v)))

(defn schema-validate
  "Like m/validate but replaces RenderFn records with a
   plain fn before validating so they pass as 'fn?"
  [schema v]
  (m/validate schema (coerce-validation-input schema v)))

(defn diff-unwrap
  "Unwrap a diff record: returns [inner-value diff-status]"
  [v]
  (cond
    (insertion? v) [(:+ v) :inserted]
    (deletion? v) [(:- v) :deleted]
    (mismatch? v) [(:+ v) :changed]
    :else [v nil]))

(defn preserve-meta
  [source target]
  (if-some [m (meta source)]
    (with-meta target m)
    target))

(defn replace-index
  [v i item]
  (let [v (vec v)]
    (preserve-meta v
                   (vec (concat (subvec v 0 i)
                                [item]
                                (subvec v (inc i)))))))

(defn assoc-map-value
  [m k v]
  (let [m (or m {})
        present? (contains? m k)
        rebuilt (reduce-kv (fn [acc key val]
                             (assoc acc key (if (= key k) v val)))
                           (empty m)
                           m)]
    (preserve-meta m
                   (if present?
                     rebuilt
                     (assoc rebuilt k v)))))

(defn dissoc-map-value
  [m k]
  (let [m (or m {})]
    (preserve-meta m
                   (reduce-kv (fn [acc key val]
                                (if (= key k)
                                  acc
                                  (assoc acc key val)))
                              (empty m)
                              m))))

(defn strip-diff-records
  "Recursively strip diff records from a value, producing a clean value"
  [v]
  (cond
    (mismatch? v) (strip-diff-records (:+ v))
    (insertion? v) (strip-diff-records (:+ v))
    (deletion? v) nil
    (record? v) v ;; preserve record types like RenderFn/WorkflowFn
    (map? v) (preserve-meta v
                            (into {} (keep (fn [[k v]]
                                             (let [k (cond (insertion? k) (:+ k)
                                                           (deletion? k) nil
                                                           :else k)]
                                               (when (and k (not (deletion? v)))
                                                 [k (strip-diff-records v)]))))
                                  v))
    (set? v) (preserve-meta v
                            (into #{} (comp (remove deletion?) (map strip-diff-records)) v))
    (sequential? v) (preserve-meta v
                                   (->> v (remove deletion?) (mapv strip-diff-records)))
    :else v))

(defn strip-diff-records-old
  "Like strip-diff-records but extracts the old value instead of new"
  [v]
  (cond
    (mismatch? v) (:- v)
    (insertion? v) nil
    (deletion? v) (:- v)
    (record? v) v
    (map? v) (preserve-meta v
                            (into {} (keep (fn [[k v]]
                                             (let [k (cond (insertion? k) nil
                                                           (deletion? k) (:- k)
                                                           :else k)
                                                   v (strip-diff-records-old v)]
                                               (when (and k (some? v)) [k v]))))
                                  v))
    :else v))

(defn- strip-blank-optional-entries
  "Recursively remove optional map entries whose values are nil or blank strings.
   An empty optional field (e.g. :string+) is treated as absent, so validation
   does not report spurious errors before the user blurs and the attribute is
   dissoc'd."
  [schema v]
  (let [schema (some-> schema resolved-schema)
        schema-type (some-> schema m/type)]
    (cond
      (and (= :map schema-type) (map? v))
      (reduce (fn [acc [attr opts child-schema]]
                (if-not (contains? acc attr)
                  acc
                  (let [child-v (strip-blank-optional-entries child-schema (get acc attr))]
                    (if (and (:optional opts)
                             (or (nil? child-v)
                                 (and (string? child-v) (str/blank? child-v))))
                      (dissoc acc attr)
                      (assoc acc attr child-v)))))
              v
              (m/children schema))

      (and (contains? #{:vector :sequential} schema-type) (sequential? v))
      (let [inner (first (m/children schema))]
        (mapv #(strip-blank-optional-entries inner %) v))

      (and (= :set schema-type) (set? v))
      (let [inner (first (m/children schema))]
        (into #{} (map #(strip-blank-optional-entries inner %)) v))

      (= :or schema-type)
      (let [children (vec (m/children schema))]
        (if-let [idx (or-matching-index children v)]
          (strip-blank-optional-entries (nth children idx nil) v)
          v))

      :else v)))

(defn schema-explain
  "Like m/explain but validates the current value after diff records and
   render-function wrappers have been normalized away."
  [schema v]
  (let [value (strip-blank-optional-entries
               schema
               (coerce-validation-input schema (strip-diff-records v)))
        explain (m/explain schema value)]
    (when-let [errors (seq (:errors explain))]
      (letfn [(next-in [in expected]
                (if (= expected (first in))
                  (rest in)
                  in))
              (error-in-selected-or-branch?
                [schema value path in]
                (let [schema (resolved-schema schema)
                      path (seq path)
                      in (seq in)]
                  (cond
                    (or (nil? schema) (empty? path))
                    true

                    :else
                    (case (m/type schema)
                      :or
                      (let [children (vec (m/children schema))
                            branch-idx (first path)
                            selected-idx (or-matching-index children value)]
                        (if (number? branch-idx)
                          (and (or (nil? selected-idx)
                                   (= branch-idx selected-idx))
                               (if-let [child-schema (nth children branch-idx nil)]
                                 (error-in-selected-or-branch? child-schema value (rest path) in)
                                 true))
                          true))

                      :and
                      (let [children (vec (m/children schema))
                            child-idx (first path)]
                        (if (number? child-idx)
                          (if-let [child-schema (nth children child-idx nil)]
                            (error-in-selected-or-branch? child-schema value (rest path) in)
                            true)
                          true))

                      :map
                      (let [attr (first path)
                            child-schema (some (fn [[child-attr _ child-schema]]
                                                 (when (= attr child-attr)
                                                   child-schema))
                                               (m/children schema))]
                        (if child-schema
                          (error-in-selected-or-branch? child-schema
                                                        (get value attr)
                                                        (rest path)
                                                        (next-in in attr))
                          true))

                      (:vector :sequential)
                      ;; :vector/:sequential schema path always uses child position 0
                      ;; (there's one child schema); the actual value index lives in :in.
                      (let [item-idx (first in)
                            child-schema (first (m/children schema))
                            value (when (sequential? value) (vec value))
                            child-value (when (and child-schema
                                                   (number? item-idx)
                                                   value
                                                   (<= 0 item-idx)
                                                   (< item-idx (count value)))
                                          (nth value item-idx nil))]
                        (if (number? item-idx)
                          (error-in-selected-or-branch? child-schema
                                                        child-value
                                                        (rest path)
                                                        (rest in))
                          true))

                      :tuple
                      (let [item-idx (first path)
                            children (vec (m/children schema))
                            value (when (sequential? value) (vec value))]
                        (if (number? item-idx)
                          (if-let [child-schema (nth children item-idx nil)]
                            (error-in-selected-or-branch? child-schema
                                                          (when (and value
                                                                     (<= 0 item-idx)
                                                                     (< item-idx (count value)))
                                                            (nth value item-idx nil))
                                                          (rest path)
                                                          (next-in in item-idx))
                            true)
                          true))

                      :cat
                      (let [section-idx (first path)
                            children (vec (m/children schema))
                            sections (when (sequential? value)
                                       (decatenate-exact children (vec value)))
                            offsets (when sections
                                      (vec (reductions + 0 (map count sections))))]
                        (if (number? section-idx)
                          (if-let [child-schema (nth children section-idx nil)]
                            (let [section-value (when sections (nth sections section-idx nil))
                                  flat-idx (first in)
                                  offset (when offsets (nth offsets section-idx 0))
                                  local-in (if (and (number? flat-idx)
                                                    offset
                                                    section-value
                                                    (<= offset flat-idx)
                                                    (< flat-idx (+ offset (count section-value))))
                                             (cons (- flat-idx offset) (rest in))
                                             in)]
                              (error-in-selected-or-branch? child-schema
                                                            section-value
                                                            (rest path)
                                                            local-in))
                            true)
                          true))

                      (:+ :*)
                      ;; :+/:* schema path uses the child position 0; value index is in :in.
                      (let [item-idx (first in)
                            child-schema (first (m/children schema))
                            value (when (sequential? value) (vec value))
                            child-value (when (and child-schema
                                                   (number? item-idx)
                                                   value
                                                   (<= 0 item-idx)
                                                   (< item-idx (count value)))
                                          (nth value item-idx nil))]
                        (if (number? item-idx)
                          (error-in-selected-or-branch? child-schema
                                                        child-value
                                                        (rest path)
                                                        (rest in))
                          true))

                      :?
                      (let [value (when (sequential? value) (vec value))
                            child-schema (first (m/children schema))
                            child-value (first value)
                            in (if (and (= 1 (count value))
                                        (some? child-value)
                                        (= 0 (first in)))
                                 (rest in)
                                 in)]
                        (error-in-selected-or-branch? child-schema child-value (rest path) in))

                      :multi
                      (let [dispatch-fn (:dispatch (second (m/form schema)))
                            dispatch-value (when dispatch-fn (dispatch-fn value))
                            branches (vec (m/children schema))
                            child-idx (first path)
                            selected-idx (or (first (keep-indexed
                                                     (fn [i [branch-dispatch-value]]
                                                       (when (= branch-dispatch-value dispatch-value) i))
                                                     branches))
                                             (when (nil? dispatch-value)
                                               (first (keep-indexed
                                                       (fn [i [branch-dispatch-value]]
                                                         (when (nil? branch-dispatch-value) i))
                                                       branches))))]
                        (if (number? child-idx)
                          (and (or (nil? selected-idx)
                                   (= child-idx selected-idx))
                               (if-let [[_ _ child-schema] (nth branches child-idx nil)]
                                 (error-in-selected-or-branch? child-schema value (rest path) in)
                                 true))
                          true))

                      true))))]
        (let [filtered-errors (->> errors
                                   (filter #(error-in-selected-or-branch? schema value (:path %) (:in %)))
                                   vec)]
          (when (seq filtered-errors)
            (assoc explain :errors filtered-errors)))))))

(defn collapse-diff-map
  "Collapse a map with per-key diff records into a single atomic Mismatch."
  [m]
  (let [old-val (strip-diff-records-old m)
        new-val (strip-diff-records m)]
    (if (= old-val new-val)
      new-val
      (diff-impl/->Mismatch old-val new-val))))

(defn normalize-diff-map-keys
  "In deep-diff2 output, new/removed map keys are wrapped in Insertion/Deletion.
   Normalizes so keys are plain keywords and values carry the diff status instead."
  [value]
  (if (or (not (map? value)) (record? value))
    value
    (preserve-meta value
                   (into {}
                         (map (fn [[k v]]
                                (cond
                                  (insertion? k) [(:+ k) (diff-impl/->Insertion v)]
                                  (deletion? k)  [(:- k) (diff-impl/->Deletion v)]
                                  :else          [k v])))
                         value))))

(defn form-kw
  "For registered schema refs, m/type returns :malli.core/schema - use m/form to get the actual keyword like :rec/fn"
  [schema]
  (let [schema-type (m/type schema)]
    (if (= schema-type :malli.core/schema)
      (m/form schema)
      schema-type)))

(defn display-label [label]
  (if (or (keyword? label) (string? label) (symbol? label))
    (tr label)
    label))

(defn schema-label [schema]
  (if schema
    (let [schema (if (keyword? schema)
                   (or (m/deref (m/schema schema)) schema)
                   schema)]
      (tr (or (some-> (:editor/always-visible-label (m/properties schema)) display-label)
              (some-> (:label (m/properties schema)) display-label)
              (form-kw schema))))
    (str "-- " (pr-str schema))))

(defn resolved-schema [schema]
  (if (keyword? schema)
    (or (m/deref (m/schema schema)) schema)
    schema))

(defn schema-editor-view [schema]
  (let [schema (resolved-schema schema)
        schema-type (m/type schema)]
    (or (:editor/view (m/properties schema))
        (form->type (m/form schema))
        (:view (schema-type-defs schema-type))
        schema-type)))

(defn schema-label-key [schema]
  (let [schema (resolved-schema schema)]
    (:label-key (m/properties schema))))

(defn apply-auto-id
  "Generate automatic id values (e.g. :ductile/id) for a new/duplicated item.
   Use in schema opts: `[:map {:editor/auto-id {:ductile/id #(random-uuid)}} ,,,]`"
  [schema v]
  (if-let [auto-id (:editor/auto-id (m/properties (resolved-schema schema)))]
    (reduce-kv (fn [m k f] (assoc m k (f))) v auto-id)
    v))

(defn label-key-attrs [label-key]
  (cond
    (keyword? label-key) [label-key]
    (vector? label-key) (filter keyword? label-key)
    :else []))

(defn branch-disambiguating-attr
  [schema label-key]
  (let [schema (resolved-schema schema)]
    (when (= :map (m/type schema))
      (let [label-attrs (set (label-key-attrs label-key))
            children (m/children schema)
            candidate? (fn [[attr _ _]] (not (contains? label-attrs attr)))]
        (or (some (fn [[attr schema-opts _]]
                    (when (and (candidate? [attr schema-opts nil])
                               (not (:optional schema-opts)))
                      attr))
                  children)
            (some (fn [[attr schema-opts _]]
                    (when (candidate? [attr schema-opts nil])
                      attr))
                  children))))))

(defn label-key-value [label-key value]
  (let [value (some-> (if (deletion? value) (:- value) value)
                      strip-diff-records)]
    (when (some? value)
      (cond
        (vector? label-key) (some #(let [label (and (ifn? %) (% value))]
                                     (when-not (str/blank? (str/trim (str label))) label))
                                  label-key)
        (ifn? label-key) (label-key (cond-> value
                                      (and (symbol? value) (= tr label-key))
                                      keyword))
        :else nil))))

(defn simple-readonly-value [{:as opts :keys [value schema-properties]}]
  (let [label-key (or (:label-keys schema-properties)
                      (:label-key schema-properties))
        display (if label-key
                  (fn [v] (or (label-key-value label-key v)
                              (pr-str v)))
                  fahrzeug/inspect-value)]
    (cond
      (mismatch? value)
      [:span.inline-flex.items-center.gap-1
       [:span.line-through.hover:no-underline.text-red-500.font-medium.opacity-70 (display (:- value))]
       [:span.text-slate-600 [ui/icon {:size 10} :arrow-right]]
       [:span.text-green-700.font-medium (display (:+ value))]]

      (insertion? value)
      [:span.text-green-700.font-medium (display (:+ value))]

      (deletion? value)
      [:span.line-through.hover:no-underline.text-red-500.font-medium.opacity-70 (display (:- value))]

      :else
      (display value))))

(defn inst-readonly-value [{:keys [value]}]
  (let [display (fn [v] (when (inst? v)
                          [:span.whitespace-nowrap.tabular-nums
                           (time-format/->str {:format :date} v)]))]
    (cond
      (mismatch? value)
      [:span.inline-flex.items-center.gap-1
       [:span.line-through.hover:no-underline.text-red-500.font-medium.opacity-70 (display (:- value))]
       [:span.text-slate-600 [ui/icon {:size 10} :arrow-right]]
       [:span.text-green-700.font-medium (display (:+ value))]]

      (insertion? value)
      [:span.text-green-700.font-medium (display (:+ value))]

      (deletion? value)
      [:span.line-through.hover:no-underline.text-red-500.font-medium.opacity-70 (display (:- value))]

      :else
      (display value))))

(defn cas-file-diff-item [cas-file]
  (let [cas-file (normalize-diff-map-keys cas-file)]
    (if (and (map? cas-file)
             (not (record? cas-file))
             (some diff-record? (vals cas-file)))
      (collapse-diff-map cas-file)
      cas-file)))

(defn cas-file-render-value
  ([cas-file] (cas-file-render-value cas-file nil))
  ([cas-file status]
   (let [cas-file (cas-file-diff-item cas-file)]
     (case status
       :deleted (diff-record-old-value cas-file)
       (diff-record-new-value cas-file)))))

(defn cas-file-label [cas-file]
  (or (:file/name (cas-file-render-value cas-file)) (tr "Document")))

(defn cas-file-link-class [status]
  (case status
    :inserted ["text-green-700" "hover:text-green-700" "focus-visible:text-green-700" "active:text-green-800"]
    :deleted ["text-red-500" "hover:text-red-500" "focus-visible:text-red-500" "active:text-red-600"]
    nil))

(defn cas-file-link
  ([cas-file] (cas-file-link cas-file nil))
  ([cas-file {:keys [status]}]
   (let [class (cas-file-link-class status)
         cas-file (cas-file-render-value cas-file status)]
     (if (and (map? cas-file) (:cas/multihash cas-file))
       [fahrzeug/file-view {:class class} cas-file]
       [:span {:class (ui/class-vec "text-slate-500" class)}
        (cas-file-label cas-file)]))))

(def structural-views #{:map :list :set :cat :map-of :tuple :locations :location :or})

(defn structural-editor-view
  [schema]
  (let [schema (resolved-schema schema)
        schema (or (structural-child schema) schema)
        editor-view (some-> schema schema-editor-view)]
    (when (contains? structural-views editor-view)
      editor-view)))

(defn has-children-to-delete?
  [editor-view value]
  (case editor-view
    :map (some? value)
    :location (some? value)
    :or (some? value)
    (:list :set :cat :map-of :tuple :locations) (boolean (seq value))
    false))

(defn collapsible-entry-count
  [schema-type schema value]
  (when-let [{:keys [count-by]} (schema-type-defs schema-type)]
    (case count-by
      :value (count value)
      :schema (count (m/children schema))
      nil)))

(defn structural-child
  "For :and schemas, find the actual schema child that is not a pred/validation fn"
  [schema]
  (when (= :and (m/type schema))
    (->> (m/children schema)
         (remove #(or (symbol? (m/type %))
                      (#{:fn} (m/type %))))
         first)))

(defn has-structural-content?
  [editor-view schema value]
  (let [schema (resolved-schema schema)
        schema (or (structural-child schema) schema)]
    (case editor-view
      (:map :map-of :list :set :cat :locations :location) (boolean (seq value))
      :tuple (or (boolean (seq value))
                 (boolean (seq (m/children schema))))
      :or (some? value)
      false)))

(defn edit-target-value
  [value]
  (let [value (strip-diff-records (if (deletion? value) (:- value) value))]
    (cond-> value
      (map? value) normalize-diff-map-keys)))

(defn editable-label-key-attr [label-key value]
  (let [value (edit-target-value value)]
    (when (map? value)
      (or (some #(when (contains? value %) %) (label-key-attrs label-key))
          (first (label-key-attrs label-key))))))

(defn promoted-label-attr
  [label-key value]
  (let [value (edit-target-value value)]
    (when (map? value)
      (or (some (fn [attr]
                  (when (label-key-value [attr] value)
                    attr))
                (label-key-attrs label-key))
          (editable-label-key-attr label-key value)))))

(defn path-editable?
  [editable-paths path]
  (or (nil? editable-paths)
      (some #(= % (take (count %) path)) editable-paths)))

(defn path-active?
  [path active-path]
  (and (some? active-path)
       (= path (take (count path) active-path))))

(def text-editor-views
  #{:string :textarea :re :keyword})

(def edit-form-font-class "text-[12px]")

(defn drop-optional-empty-text-field?
  [schema-opts editor-view v]
  (and (:optional schema-opts)
       (contains? text-editor-views editor-view)
       (or (nil? v)
           (and (string? v) (str/blank? v)))))

(defn collapsible-meta
  [{:keys [schema editor-view value show-count?]}]
  (let [schema (resolved-schema schema)
        collapse-schema (or (structural-child schema) schema)
        collapse-view (or (when (contains? structural-views editor-view)
                            editor-view)
                          (let [structural-view (schema-editor-view collapse-schema)]
                            (when (contains? structural-views structural-view)
                              structural-view)))
        collapse-type (m/type collapse-schema)
        raw-child-count (when collapse-view
                          (collapsible-entry-count collapse-type collapse-schema value))]
    {:collapse-view collapse-view
     :child-count (when (and show-count?
                             (not= collapse-view :map)
                             (pos? (or raw-child-count 0)))
                    raw-child-count)
     :has-children? (or (pos? (or raw-child-count 0))
                        (and collapse-view
                             (has-structural-content? collapse-view collapse-schema value)))
     :many-children? (> (or raw-child-count 0) 3)}))

(defn has-collapsible-content?
  [schema editor-view value]
  (:has-children? (collapsible-meta {:schema schema
                                     :editor-view editor-view
                                     :value value})))

(defn map-reserve-toggle-gutter?
  [{:keys [schema value path hidden-attrs hidden-paths suppress-promoted-label? editing?]}]
  (when-some [schema (resolved-schema schema)]
    (let [path (vec (or path []))
          value (normalize-diff-map-keys (or value {}))
          hidden-paths (into (set (or hidden-paths #{}))
                             (map #(conj path %))
                             (or hidden-attrs #{}))
          render-hidden-paths (into hidden-paths
                                    (map #(conj path %))
                                    (when (and suppress-promoted-label? (not editing?))
                                      (some-> (promoted-label-attr (schema-label-key schema) value)
                                              vector)))]
      (and (not editing?)
           (some (fn [[attr schema-opts schema-type]]
                   (when (and (not (:editor/hidden? schema-opts))
                              (not (:editor/demoted? schema-opts))
                              (not (contains? render-hidden-paths (conj path attr)))
                              (contains? value attr))
                     (let [child-schema (resolved-schema schema-type)
                           editor-view (or (:editor/view schema-opts)
                                           (schema-editor-view child-schema))]
                       (has-collapsible-content? child-schema editor-view (get value attr)))))
                 (m/children schema))))))

(defn or-map-fallback-index
  [or-children plain-value]
  (when (map? plain-value)
    (let [present-attrs (set (keys plain-value))]
      (some->> or-children
               (keep-indexed
                (fn [i child-schema]
                  (let [child-schema (resolved-schema child-schema)]
                    (when (= :map (m/type child-schema))
                      (let [child-fields (m/children child-schema)
                            shared-count (count (filter (comp present-attrs first) child-fields))
                            missing-required-count (count
                                                    (remove #(contains? present-attrs (first %))
                                                            (remove (comp :optional second) child-fields)))]
                        [i [shared-count (- missing-required-count) (- (count child-fields))]])))))
               (reduce (fn [best candidate]
                         (if (or (nil? best)
                                 (pos? (compare (second candidate) (second best))))
                           candidate
                           best))
                       nil)
               first))))

(defn or-matching-index
  [or-children plain-value]
  (or (::or-index (meta plain-value))
      (first (keep-indexed
              (fn [i child-schema]
                (when (schema-validate child-schema plain-value) i))
              or-children))
      (or-map-fallback-index or-children plain-value)))

(defn promoted-label-path
  [path label-key value]
  (when-some [attr (promoted-label-attr label-key value)]
    (conj path attr)))

(defn first-edit-target-path
  [{:keys [schema value path editor-view preferred-path hidden-paths]}]
  (let [schema (resolved-schema schema)
        path (vec (or path []))
        editor-view (or editor-view (schema-editor-view schema))
        hidden-paths (or hidden-paths #{})
        value (edit-target-value value)]
    (cond
      preferred-path preferred-path
      (not (contains? structural-views editor-view)) path
      (= :and (m/type schema)) (if-let [inner (structural-child schema)]
                                 (first-edit-target-path {:schema inner
                                                          :value value
                                                          :path path
                                                          :hidden-paths hidden-paths})
                                 path)
      (= :or editor-view)
      (let [or-children (vec (m/children schema))
            matching-index (or-matching-index or-children value)]
        (or (promoted-label-path path (schema-label-key schema) value)
            (when-some [active-schema (when-some [index matching-index]
                                        (nth or-children index nil))]
              (first-edit-target-path {:schema active-schema
                                       :value value
                                       :path path
                                       :hidden-paths hidden-paths}))
            path))
      (= :map editor-view)
      (let [visible-children (remove (fn [[attr schema-opts]]
                                       (or (:editor/hidden? schema-opts)
                                           (:editor/demoted? schema-opts)
                                           (contains? hidden-paths (conj path attr))))
                                     (m/children schema))
            children-by-attr (into {} (map (juxt first identity)) visible-children)
            label-attr (promoted-label-attr (schema-label-key schema) value)]
        (if-let [[attr _ child-schema]
                 (or (some->> label-attr
                              (get children-by-attr))
                     (first (filter #(contains? value (first %)) visible-children)))]
          (first-edit-target-path {:schema child-schema
                                   :value (get value attr)
                                   :path (conj path attr)
                                   :hidden-paths hidden-paths})
          path))
      (= :list editor-view) (if-let [item (first (seq value))]
                              (first-edit-target-path {:schema (first (m/children schema))
                                                       :value item
                                                       :path (conj path 0)
                                                       :hidden-paths hidden-paths})
                              path)
      (= :tuple editor-view) (if-let [child-schema (first (m/children schema))]
                               (first-edit-target-path {:schema child-schema
                                                        :value (first value)
                                                        :path (conj path 0)
                                                        :hidden-paths hidden-paths})
                               path)
      :else path)))

(defn edit-on-double-click-props
  [{:keys [edit! editable-paths editing? editing-path schema value path editor-view preferred-path hidden-paths]}]
  (when (and edit!
             (not editing?)
             (nil? editing-path))
    (let [path (vec (or path []))
          target-path (->> [preferred-path
                            (first-edit-target-path {:schema schema
                                                     :value value
                                                     :path path
                                                     :editor-view editor-view
                                                     :preferred-path preferred-path
                                                     :hidden-paths hidden-paths})
                            path]
                           (remove nil?)
                           distinct
                           (filter #(path-editable? editable-paths %))
                           first)]
      (when target-path
        {:on-double-click (fn [e]
                            (.preventDefault e)
                            (.stopPropagation e)
                            (edit! target-path))}))))

(defn default-value
  [schema]
  (let [schema (if (keyword? schema)
                 (m/schema schema)
                 schema)
        schema-type (m/type schema)]
    (case schema-type
      := (first (m/children schema))
      zero? 0
      (:nil nil?) nil
      :boolean false
      :map {}
      (:vector :sequential :set) []
      :tuple (vec (map default-value (m/children schema)))
      :and (some-> (structural-child schema) default-value)
      :or (when-let [child-schema (first (m/children schema))]
            (let [v (default-value child-schema)]
              (cond-> v
                (map? v) (vary-meta assoc ::or-index 0))))
      :multi (when-let [child-schema (or (some (fn [[dispatch-value _ child-schema]]
                                                 (when (nil? dispatch-value)
                                                   child-schema))
                                               (m/children schema))
                                         (some-> (m/children schema) first (nth 2 nil)))]
               (default-value child-schema))
      nil)))


(defn ->selector
  ([path] (->selector "path" path))

  ([k path]
   (str "[data-" k "='" (pr-str path) "']")))

(defn value-path-exists?
  [value path]
  (loop [value (edit-target-value value)
         path (seq path)]
    (if (empty? path)
      true
      (let [segment (first path)
            value (cond
                    (map? value) (normalize-diff-map-keys value)
                    (sequential? value) (vec value)
                    :else value)]
        (cond
          (map? value)
          (when (contains? value segment)
            (recur (get value segment) (rest path)))

          (sequential? value)
          (when (and (number? segment)
                     (<= 0 segment)
                     (< segment (count value)))
            (recur (nth value segment nil) (rest path)))

          :else
          false)))))

(defn closest-existing-value-path
  [value path]
  (loop [path (vec (or path []))]
    (cond
      (value-path-exists? value path) path
      (seq path) (recur (pop path))
      :else [])))

(defn exact-rendered-path-target
  [path]
  (let [path (vec (or path []))
        row-selector (->selector "row" path)
        path-selector (->selector path)]
    (cond
      (js/document.querySelector row-selector)
      {:path path :selector row-selector}

      (js/document.querySelector path-selector)
      {:path path :selector path-selector}

      :else
      nil)))

(defn closest-rendered-parent-target
  [path]
  (loop [path (vec (or path []))]
    (if (seq path)
      (let [path (pop path)
            row-selector (->selector "row" path)
            path-selector (->selector path)]
        (cond
          (js/document.querySelector row-selector)
          {:path path :selector row-selector}

          (js/document.querySelector path-selector)
          {:path path :selector path-selector}

          :else
          (recur path)))
      nil)))

(defn with-reveal-target!
  ([path callback] (with-reveal-target! path callback 8))
  ([path callback retries]
   (if-let [{:keys [selector]} (exact-rendered-path-target path)]
     (ui/with-element! selector callback)
     (if (pos? retries)
       (js/requestAnimationFrame #(with-reveal-target! path callback (dec retries)))
       (if-let [{:keys [selector]} (closest-rendered-parent-target path)]
         (ui/with-element! selector callback)
         (ui/with-element! (->selector path) callback))))))

(defn closest-rendered-path-target
  [path]
  (loop [path (vec (or path []))]
    (let [row-selector (->selector "row" path)
          path-selector (->selector path)]
      (cond
        (js/document.querySelector row-selector)
        {:path path :selector row-selector}

        (js/document.querySelector path-selector)
        {:path path :selector path-selector}

        (seq path)
        (recur (pop path))

        :else
        nil))))

(defn flash!
  "Flash an element or path with a color overlay that fades out"
  [el-or-selector & {:keys [color promise-delay duration]
                     :or {color "rgb(224 231 255)" ; indigo-100
                          promise-delay 300 duration 900}}]
  (js/Promise.
   (fn [resolve]
     (js/requestAnimationFrame
      #(let [el (cond-> el-or-selector (string? el-or-selector) (js/document.querySelector))]
         (if el
           (do
             (js/console.log "flash!" el)
             (let [original-transition (.. el -style -transition)
                   original-bg (.. el -style -backgroundColor)]
               (set! (.. el -style -transition) "background-color 0s")
               (set! (.. el -style -backgroundColor) color)
               (js/setTimeout resolve promise-delay)
               (js/requestAnimationFrame
                (fn []
                  (set! (.. el -style -transition) (str "background-color " duration "ms"))
                  (set! (.. el -style -backgroundColor) "transparent")
                  (js/setTimeout
                   (fn []
                     (when-let [el (cond-> el-or-selector (string? el-or-selector) (js/document.querySelector))]
                       (set! (.. el -style -transition) original-transition)
                       (set! (.. el -style -backgroundColor) original-bg)))
                   duration)))))
           (resolve)))))))

(defn use-expanded-state
  [path has-children? start-collapsed? auto-expand? lock-expanded?]
  (let [!expanded? (hooks/use-state (or auto-expand? lock-expanded? (not start-collapsed?)))]
    (hooks/use-effect
     (fn []
       (when (or auto-expand? lock-expanded?)
         (reset! !expanded? true)))
     [auto-expand? lock-expanded?])
    {:expanded? (or lock-expanded? (not has-children?) @!expanded?)
     :toggle-expanded! (fn [& _]
                         (let [was-collapsed? (not @!expanded?)]
                           (swap! !expanded? not)
                           (when was-collapsed?
                             (ui/with-element!
                               (->selector path)
                               (fn [el]
                                 (ui/scroll-into-view-with-margin! el {:smooth? true})
                                 (flash! el))))))}))

(defn focus-path!
  "Focus the first form input within the editor at the given path.
   Also scrolls the element into view and flashes a highlight.
   When :after-el is provided, only focuses inputs that come after that element in DOM order.
   When :delay is provided, waits that many ms before focusing (useful when editing path
   changes simultaneously, to let React re-render and layout settle first)."
  [path & {:keys [after-el delay]}]
  (let [do-focus (fn []
                   (ui/with-element!
                     (->selector path)
                     (fn [el]
                       (let [input-selector "input, select, textarea, [contenteditable]"
                             input (if after-el
                                     (->> (.querySelectorAll el input-selector)
                                          (drop-while #(not (pos? (.compareDocumentPosition after-el %))))
                                          first)
                                     (.querySelector el input-selector))]
                         (when input (.focus input))
                         (ui/scroll-into-view-with-margin! el {:smooth? true})
                         (js/setTimeout #(flash! el) 350)))))]
    (if delay
      (js/setTimeout do-focus delay)
      (do-focus))))

(defn reveal-path!
  "Scroll the editor row for the given path into view and flash it."
  [path & {:keys [delay]}]
  (let [do-reveal (fn []
                    (with-reveal-target!
                      path
                      (fn [el]
                        (ui/scroll-into-view-with-margin! el {:smooth? true})
                        (js/setTimeout #(flash! el) 100))))]
    (if delay
      (js/setTimeout do-reveal delay)
      (do-reveal))))

(defn highlight!
  ([k path] (highlight! k path "bg-indigo-50"))
  ([k path classes]
   (when-let [el (js/document.querySelector (->selector k path))]
     (let [classes (if (string? classes) [classes] classes)]
       (apply js-invoke (.-classList el) "add" classes)))))

(defn unhighlight!
  ([k path] (unhighlight! k path "bg-indigo-50"))
  ([k path classes]
   (when-let [el (js/document.querySelector (->selector k path))]
     (let [classes (if (string? classes) [classes] classes)]
       (apply js-invoke (.-classList el) "remove" classes)))))

(defn flash-delete!
  [el-or-path]
  (flash! (cond-> el-or-path (vector? el-or-path) (->selector))
          :color "rgb(254 202 202)" ;; red-200
          :promise-delay 70
          :duration 400))

(defn editor-input-target?
  [^js target]
  (or (= "INPUT" (some-> target .-tagName))
      (= "SELECT" (some-> target .-tagName))
      (= "TEXTAREA" (some-> target .-tagName))
      (true? (.-isContentEditable target))
      (some-> target (.closest ".cm-editor"))))

(defn flush-active-input!
  "Blur the currently focused editor input and wait a tick so blur handlers commit."
  []
  (if-let [^js active-el js/document.activeElement]
    (if (editor-input-target? active-el)
      (js/Promise.
       (fn [resolve]
         (.blur active-el)
         (js/requestAnimationFrame #(resolve nil))))
      (js/Promise.resolve nil))
    (js/Promise.resolve nil)))

(defn blur-active-input-on-mouse-down!
  [^js e]
  (when-let [^js active-el js/document.activeElement]
    (when (editor-input-target? active-el)
      ;; Keep action links from stealing focus before blur commits the pending edit.
      (.preventDefault e)
      (.blur active-el))))

(defn diff-row-class [status]
  (case status
    :inserted "text-green-700 font-medium"
    :deleted  "text-red-500 font-medium opacity-70"
    nil))

(def breadcrumb-collapse-threshold 3)
(def breadcrumb-visible-tail-count 2)

(defn breadcrumb-label-text [label]
  (let [label (display-label label)]
    (cond
      (nil? label) nil
      (string? label) (not-empty (str/trim label))
      :else (str label))))

(defn path-segment-label
  [seg]
  (cond
    (number? seg) (tr "Item %1" (inc seg))
    :else (or (breadcrumb-label-text seg)
              (str seg))))

(defn schema-explicit-label
  [schema]
  (when-let [schema (resolved-schema schema)]
    (some-> (or (:editor/always-visible-label (m/properties schema))
                (:label (m/properties schema)))
            breadcrumb-label-text)))

(defn schema-preview-label
  [schema value]
  (let [value (edit-target-value value)]
    (when-let [label-key (schema-label-key schema)]
      (some-> (label-key-value label-key value)
              breadcrumb-label-text))))

(defn multi-active-child-schema
  [schema value]
  (let [value (edit-target-value value)
        dispatch-fn (:dispatch (second (m/form schema)))
        dispatch-value (when dispatch-fn (dispatch-fn value))
        branches (vec (m/children schema))]
    (or (some (fn [[branch-dispatch-value _ child-schema]]
                (when (= branch-dispatch-value dispatch-value)
                  child-schema))
              branches)
        (when (nil? dispatch-value)
          (some (fn [[branch-dispatch-value _ child-schema]]
                  (when (nil? branch-dispatch-value)
                    child-schema))
                branches)))))

(defn active-breadcrumb-schema
  [schema value]
  (loop [schema (resolved-schema schema)]
    (let [schema-type (some-> schema m/type)]
      (cond
        (= :and schema-type)
        (if-let [inner (structural-child schema)]
          (recur inner)
          schema)

        (= :or schema-type)
        (if-let [idx (or-matching-index (vec (m/children schema))
                                        (edit-target-value value))]
          (if-let [child-schema (nth (m/children schema) idx nil)]
            (recur child-schema)
            schema)
          schema)

        (= :multi schema-type)
        (if-let [child-schema (multi-active-child-schema schema value)]
          (recur child-schema)
          schema)

        :else schema))))

(defn breadcrumb-segment-descriptor
  [schema value seg]
  (let [schema (active-breadcrumb-schema schema value)
        value (edit-target-value value)
        schema-type (some-> schema m/type)]
    (cond
      (= :map schema-type)
      (let [[_ schema-opts child-schema] (some #(when (= seg (first %)) %)
                                               (m/children schema))]
        {:label (or (some-> schema-opts :label breadcrumb-label-text)
                    (path-segment-label seg))
         :schema child-schema
         :value (get value seg)})

      (contains? #{:vector :sequential :set :+ :*} schema-type)
      (let [items (when (sequential? value) (vec value))
            child-schema (first (m/children schema))
            child-value (when (and (number? seg)
                                   items
                                   (<= 0 seg)
                                   (< seg (count items)))
                          (nth items seg nil))]
        {:label (or (schema-preview-label child-schema child-value)
                    (path-segment-label seg))
         :schema child-schema
         :value child-value})

      (= :tuple schema-type)
      (let [children (vec (m/children schema))
            child-schema (when (number? seg) (nth children seg nil))
            items (when (sequential? value) (vec value))
            child-value (when (and (number? seg)
                                   items
                                   (<= 0 seg)
                                   (< seg (count items)))
                          (nth items seg nil))]
        {:label (or (schema-preview-label child-schema child-value)
                    (schema-explicit-label child-schema)
                    (path-segment-label seg))
         :schema child-schema
         :value child-value})

      (= :map-of schema-type)
      (let [[key-schema value-schema] (m/children schema)
            entries (if (map? value)
                      (vec (map (fn [[k v]] {:key k :value v}) value))
                      (when (sequential? value) (vec value)))
            child-schema [:map [:key key-schema] [:value value-schema]]
            child-value (when (and (number? seg)
                                   entries
                                   (<= 0 seg)
                                   (< seg (count entries)))
                          (nth entries seg nil))]
        {:label (or (schema-preview-label child-schema child-value)
                    (path-segment-label seg))
         :schema child-schema
         :value child-value})

      (= :cat schema-type)
      (let [children (vec (m/children schema))
            child-schema (when (number? seg) (nth children seg nil))
            sections (when (sequential? value)
                       (decatenate-exact children (vec value)))
            section-value (when (and (number? seg)
                                     sections
                                     (<= 0 seg)
                                     (< seg (count sections)))
                            (nth sections seg nil))
            explicit-label (schema-explicit-label child-schema)
            repeat-section? (contains? #{:+ :* :?} (some-> child-schema resolved-schema m/type))]
        {:label (when-not (and repeat-section? (nil? explicit-label))
                  (or explicit-label
                      (path-segment-label seg)))
         :schema child-schema
         :value section-value})

      (= :? schema-type)
      (let [child-schema (first (m/children schema))
            child-value (cond
                          (and (sequential? value) (seq value)) (first value)
                          (number? seg) nil
                          :else value)
            explicit-label (schema-explicit-label child-schema)
            preview-label (schema-preview-label child-schema child-value)]
        {:label (or preview-label
                    explicit-label)
         :schema child-schema
         :value child-value})

      :else
      {:label (path-segment-label seg)
       :schema nil
       :value nil})))

(defn breadcrumb-segments
  [schema value path]
  (loop [segments []
         schema schema
         value value
         [seg & more] (vec (or path []))]
    (if-not seg
      segments
      (let [{:keys [label schema value]} (breadcrumb-segment-descriptor schema value seg)]
        (recur (cond-> segments label (conj label))
               schema
               value
               more)))))

(defn collapsed-breadcrumb-segments
  [segments]
  (let [segments (vec segments)
        collapsed? (> (count segments) breadcrumb-collapse-threshold)]
    {:segments (if collapsed?
                 (into ["…"] (take-last breadcrumb-visible-tail-count segments))
                 segments)
     :collapsed? collapsed?}))

(defn breadcrumbs
  [path-or-opts]
  (let [{:keys [path schema value last-segment-label]}
        (if (and (map? path-or-opts)
                 (or (contains? path-or-opts :path)
                     (contains? path-or-opts :schema)
                     (contains? path-or-opts :value)
                     (contains? path-or-opts :last-segment-label)))
          path-or-opts
          {:path path-or-opts})
        path (vec (or path []))
        raw-segments (if schema
                       (breadcrumb-segments schema value path)
                       (mapv path-segment-label path))
        full-segments (cond-> (vec raw-segments)
                        (and last-segment-label (seq raw-segments))
                        (assoc (dec (count raw-segments)) last-segment-label))
        {:keys [segments collapsed?]} (collapsed-breadcrumb-segments full-segments)
        title (when collapsed?
                (str/join " / " full-segments))]
    (into [:span (cond-> {}
                   title (assoc :title title))]
          (comp
           (map-indexed
            (fn [i seg]
              ^{:key i}
              [:span.whitespace-nowrap seg]))
           (interpose [:span.text-slate-300 " / "]))
          segments)))

(defn path-summary-text
  [schema path]
  (if (seq path)
    (str/join " / " (map path-segment-label path))
    (str (schema-label schema))))

(defn flatten-validation-messages
  [x]
  (cond
    (nil? x) []
    (string? x) [x]
    (keyword? x) [(tr x)]
    (map? x) (mapcat flatten-validation-messages (vals x))
    (sequential? x) (mapcat flatten-validation-messages x)
    :else [(str x)]))

(defn validation-error-messages
  [explain error]
  (let [path (vec (or (:in error) []))
        humanized (locale/malli-humanize (assoc explain :errors [error]))
        path-messages (if (seq path)
                        (get-in humanized path)
                        humanized)]
    (or (not-empty (vec (distinct (flatten-validation-messages path-messages))))
        (not-empty (vec (distinct (flatten-validation-messages humanized))))
        [(pr-str (select-keys error [:type :schema]))])))

(defn validation-issues
  [schema value]
  (when-let [explain (schema-explain schema value)]
    (->> (:errors explain)
         (group-by (comp vec #(or % []) :in))
         (map (fn [[path errors]]
                {:path path
                 :messages (->> errors
                                (mapcat #(validation-error-messages explain %))
                                distinct
                                vec)
                 :errors (vec errors)}))
         (sort-by (fn [{:keys [path]}] [(count path) (pr-str path)]))
         vec)))

(defn validation-summary
  [{:keys [schema issues on-select-path]}]
  (let [!details-visible? (hooks/use-state false)
        total-issues (count issues)
        preview-count 3
        preview-issues (if @!details-visible? issues (take preview-count issues))
        has-hidden-issues? (> total-issues preview-count)]
    [:div {:class "rounded-md border border-red-200 bg-red-50/80 p-1.5 text-xs leading-tight"
           :data-testid "validation-summary"}
     [:div {:class "flex items-center justify-between gap-2 pl-1 pb-1"}
      [:span.font-semibold.text-red-700 {:class "text-[11px]"} (trn ["Validation error" "%1 Validation errors"] total-issues)]
      (when has-hidden-issues?
        [:<> " · "
         [:button
          {:class "cursor-pointer whitespace-nowrap text-[11px] text-red-700 underline underline-offset-2 hover:text-red-800"
           :type "button"
           :on-click #(swap! !details-visible? not)}
          (tr (if @!details-visible? "Show less" "Show more"))]])]

     [:div {:class "flex flex-wrap gap-1"}
      (doall (for [{:keys [path messages]} preview-issues]
               (let [path-str (pr-str path)]
                 [:button
                  {:key path-str
                   :title path-str
                   :class "max-w-full rounded border border-red-200 bg-white px-2 py-1 text-left text-[11px] text-red-800 hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-red-400"
                   :data-testid (str "validation-issue-" path-str)
                   :type "button"
                   :on-click #(on-select-path path)}
                  [:span.font-medium (path-summary-text schema path)]
                  (when-let [message (first messages)]
                    [:span {:class "text-red-600/90"}
                     (str " · " message)])
                  (when (> (count messages) 1)
                    [:span {:class "text-red-500/90"}
                     (str " +" (dec (count messages)))])])))]]))

(defn checkbox [props label]
  [:label
   {:class ["flex items-center gap-2 rounded p-2 cursor-pointer hover:bg-slate-50 hover:text-indigo-600 active:bg-slate-100 border"
            edit-form-font-class]}
   [:input
    (assoc props :type :checkbox
           :class "accent-indigo-500 cursor-pointer ml-1")]
   label])

(defn button [{:as props :keys [title variant]} & content]
  (into
   [:button
    (assoc (dissoc props :variant)
           :class (str "w-fit flex items-center justify-center focus-visible:ring-2 ring-indigo-500 "
                       "font-medium leading-none border rounded px-2 py-2 text-xs gap-1 border-slate-300 "
                       (case variant
                         :primary     "bg-indigo-500 hover:bg-indigo-600 text-white"
                         :destructive "bg-white text-indigo-700 hover:text-white hover:bg-red-600 active:text-white active:bg-red-700"
                         "bg-white text-indigo-700 hover:bg-indigo-50 hover:shadow-sm active:bg-indigo-100")))]
   content))

(defn delete-button
  [{:keys [path on-click]}]
  (let [!pending? (hooks/use-state false)
        highlight-classes ["bg-slate-50" "shadow-[inset_-3px_0_0_theme(colors.red.400)]"]]
    [button
     {:tab-index -1
      :variant :destructive
      :title (tr "Delete")
      :disabled @!pending?
      :on-mouse-enter #(highlight! "row" path highlight-classes)
      :on-mouse-leave #(unhighlight! "row" path highlight-classes)
      :on-click (fn []
                  (reset! !pending? true)
                  (-> (flash-delete! path)
                      (.then on-click)
                      (.finally #(reset! !pending? false))))}
     [ui/icon :trash]]))

(defn diff-validate
  "Like m/validate but handles diff records before validating.
   For Deletions, validates the old value (since strip-diff-records returns nil).
   For Insertions/Mismatches, validates the new value."
  [schema v]
  (if (deletion? v)
    (m/validate schema (:- v))
    (m/validate schema (strip-diff-records v))))

(defn decatenate
  "Split a flat value vector into nested sections based on child schemas.
   Schemas `[:cat [:* :keyword] [:? :boolean]]` with value `[:a :b true]`
   decatenate to `[[:a :b] [true]]`"
  [child-schemas flat-value]
  (loop [schemas child-schemas
         remaining (vec flat-value)
         result []]
    (if (empty? schemas)
      result
      (let [child-schema (first schemas)
            schema-type (m/type child-schema)
            inner-schema (when (#{:+ :* :?} schema-type)
                           (first (m/children child-schema)))]
        (case schema-type
          ;; :+ (one or more) - take all consecutive matching values, min 1 slot
          :+ (let [matching (vec (take-while #(or (nil? %) (diff-validate inner-schema %)) remaining))
                   ;; if no matches, provide one default slot
                   section (if (seq matching)
                             matching
                             [(default-value inner-schema)])]
               (recur (rest schemas)
                      (vec (drop (count matching) remaining))
                      (conj result section)))

          ;; :* (zero or more) - take all consecutive matching values
          :* (let [matching (take-while #(or (nil? %) (diff-validate inner-schema %)) remaining)
                   n (count matching)]
               (recur (rest schemas)
                      (vec (drop n remaining))
                      (conj result (vec (take n remaining)))))

          ;; :? (optional) - take at most 1 if it matches
          :? (let [first-val (first remaining)
                   matches? (and (some? remaining)
                                 (seq remaining)
                                 (or (nil? first-val)
                                     (diff-validate inner-schema first-val)))]
               (recur (rest schemas)
                      (if matches? (vec (rest remaining)) remaining)
                      (conj result (if matches? [first-val] []))))

          ;; Default - take exactly 1
          (recur (rest schemas)
                 (vec (rest remaining))
                 (conj result [(first remaining)])))))))

(defn decatenate-exact
  "Split a flat value vector into nested sections without inserting editing placeholders."
  [child-schemas flat-value]
  (loop [schemas child-schemas
         remaining (vec flat-value)
         result []]
    (if (empty? schemas)
      result
      (let [child-schema (first schemas)
            schema-type (m/type child-schema)
            inner-schema (when (#{:+ :* :?} schema-type)
                           (first (m/children child-schema)))]
        (case schema-type
          :+ (let [matching (vec (take-while #(or (nil? %) (diff-validate inner-schema %)) remaining))]
               (recur (rest schemas)
                      (vec (drop (count matching) remaining))
                      (conj result matching)))

          :* (let [matching (vec (take-while #(or (nil? %) (diff-validate inner-schema %)) remaining))]
               (recur (rest schemas)
                      (vec (drop (count matching) remaining))
                      (conj result matching)))

          :? (let [first-val (first remaining)
                   matches? (and (seq remaining)
                                 (or (nil? first-val)
                                     (diff-validate inner-schema first-val)))]
               (recur (rest schemas)
                      (if matches? (vec (rest remaining)) remaining)
                      (conj result (if matches? [first-val] []))))

          (recur (rest schemas)
                 (if (seq remaining) (vec (rest remaining)) remaining)
                 (conj result (if (seq remaining) [(first remaining)] []))))))))

(def missing-cat-section-value ::missing-cat-section-value)

(defn cat-section-diff
  [child-schema old-section new-section]
  (if (= :? (m/type child-schema))
    (let [old-value (if (seq old-section) (first old-section) missing-cat-section-value)
          new-value (if (seq new-section) (first new-section) missing-cat-section-value)]
      (cond
        (and (= old-value missing-cat-section-value)
             (= new-value missing-cat-section-value))
        []

        (= old-value missing-cat-section-value)
        [(diff-impl/->Insertion new-value)]

        (= new-value missing-cat-section-value)
        [(diff-impl/->Deletion old-value)]

        :else
        [(or (ddiff/diff old-value new-value) new-value)]))
    (let [diff (ddiff/diff old-section new-section)]
      (cond
        (nil? diff) new-section
        (and (sequential? diff) (not (record? diff))) (vec diff)
        :else new-section))))

(defn cat-sections
  [child-schemas value]
  (if-not (diff-record? value)
    (decatenate child-schemas (or value []))
    (let [[old-flat new-flat] (cond
                                (mismatch? value) [(:- value) (:+ value)]
                                (insertion? value) [nil (:+ value)]
                                (deletion? value) [(:- value) nil])
          old-sections (decatenate-exact child-schemas (or old-flat []))
          new-sections (decatenate-exact child-schemas (or new-flat []))]
      (mapv cat-section-diff child-schemas old-sections new-sections))))

(defn cat-edit-sections
  [child-schemas value optional?]
  (if (and optional?
           (nil? value))
    (decatenate-exact child-schemas [])
    (decatenate child-schemas (or value []))))

(defn use-ui-value
  "Like use-state but resets to nil when value changes."
  [value]
  (let [!ui-value (hooks/use-state nil)]
    (hooks/use-effect
     (fn [] (reset! !ui-value nil))
     [value])
    !ui-value))

(defn string-editor [{:keys [value on-change schema-properties coerce lines]}]
  (let [!ui-value (use-ui-value value)]
    [ui/input
     (cond->
       {:placeholder (when-not (:optional schema-properties) (tr "missing"))
        :value (or @!ui-value (str value))
        :size 1
        :override-class "max-w-96"
        :on-change (fn [e]
                     (reset! !ui-value (.. e -target -value)))
        :on-blur (fn [_]
                   (when (some? @!ui-value)
                     (on-change (schema/coerce-or coerce @!ui-value))))}
       lines (assoc :component :textarea
                    :class "leading-normal"
                    :rows lines))]))

(defn int-editor [{:keys [value on-change schema-properties]}]
  (let [!ui-value (use-ui-value value)]
    [ui/input
     {:placeholder (tr "missing")
      :size 1
      :class (when (and (seq @!ui-value)
                        (nil? (locale/parse-int @!ui-value))) "bg-red-50")
      :value (or @!ui-value
                 (and value (locale/format-number {:maximumFractionDigits 0
                                                   :minimumFractionDigits 0}
                                                  value))
                 "")
      :on-change (fn [e]
                   (reset! !ui-value (str/replace (.. e -target -value) #"[^0-9\s]+" "")))
      :on-blur (fn [_]
                 (when (some? @!ui-value)
                   (on-change (locale/parse-int @!ui-value))))}]))

(defn format-editing-double [v]
  (let [[_ radix] (locale/current-number-separators)]
    (when (some? v)
      (-> (locale/format-number {:maximumFractionDigits 10
                                 :minimumFractionDigits 2
                                 :useGrouping false} v)
          (str/replace "." (str radix))))))

(defn double-editor [{:keys [value on-change schema-properties]}]
  (let [!ui-value (use-ui-value value)]
    [ui/input
     {:placeholder (when-not (:optional schema-properties) (tr "missing"))
      :size 1
      :class (when (and (seq @!ui-value)
                        (nil? (locale/parse-locale-double @!ui-value))) "bg-red-50")
      :value (or @!ui-value
                 (format-editing-double value)
                 "")
      :on-change (fn [e]
                   (reset! !ui-value (str/replace (.. e -target -value) #"[^0-9\.\,\s\-]+" "")))
      :on-blur (fn [_]
                 (when (some? @!ui-value)
                   (on-change (locale/parse-locale-double @!ui-value))))}]))

(defn enum-editor [{:keys [value on-change options label-key description-key]}]
  (let [label-key (or label-key tr)]
    [:div.flex.items-center.gap-1
     [combobox/view
      {:value value
       :size 1
       :width :xl
       :placeholder "-"
       :on-change #(on-change (:ui/value %))
       :choices (into []
                      (map (fn [option]
                             (cond-> {:ui/value option
                                      :ui/label (or (label-key-value label-key option)
                                                    (pr-str option))}
                               description-key
                               (assoc :ui/description (description-key option)))))
                      options)}]
     (when (some #(= value %) options)
       [ui/clear-input {:size 1 :on-click #(on-change nil)}])]))

(defn boolean-editor [{:keys [value on-change label attr]}]
  [checkbox
   {:attr attr
    :checked (boolean value)
    :on-change #(on-change (.. % -target -checked))}
   (or label (tr attr))])

(defn datetime-editor [{:keys [value on-change]}]
  [ui/input
   {:type "datetime-local"
    :size 1
    :value (or (schema/inst->str value) "")
    :on-change #(on-change (schema/coerce-or :inst (.. % -target -value)))}])

(defn date-editor [{:keys [value on-change register-pending-commit!]}]
  (let [!ui-value (use-ui-value value)
        commit! (fn []
                  (when (some? @!ui-value)
                    (on-change (ui-util/date-input-value->inst @!ui-value))))]
    (hooks/use-effect
     (fn []
       (when register-pending-commit!
         (register-pending-commit! commit!)
         #(register-pending-commit! nil)))
     [register-pending-commit! on-change])
    [:div.flex.items-center.gap-1
     [ui/input
      {:type "date"
       :size 1
       :value (or @!ui-value (ui-util/inst->date-input-value value) "")
       :on-change #(reset! !ui-value (.. % -target -value))}]
     (when value
       [ui/clear-input {:size 1 :on-click #(reset! !ui-value "")}])]))

(def month-input-supported?
  (let [el (js/document.createElement "input")]
    (set! (.-type el) "month")
    (= (.-type el) "month")))

(defn year-month-editor [{:keys [value on-change]}]
  [:div.flex.items-center.gap-1
   (if month-input-supported?
     [ui/input
      {:type "month"
       :size 1
       :value (or value "")
       :on-change #(let [v (.. % -target -value)]
                     (on-change (when (seq v) v)))}]
     [ui/input
      {:type "date"
       :size 1
       :value (when value (str value "-01"))
       :on-change #(let [v (.. % -target -value)]
                     (on-change (when (and (seq v) (>= (count v) 7))
                                  (subs v 0 7))))}])
   (when value
     [ui/clear-input {:size 1 :on-click #(on-change nil)}])])

(defn read-file-as-data-url [^js file]
  (js/Promise.
   (fn [resolve reject]
     (let [reader (js/FileReader.)]
       (set! (.-onloadend reader) #(resolve (.-result reader)))
       (set! (.-onerror reader) #(reject (or (.-error reader)
                                             (js/Error. "Unable to read file"))))
       (.readAsDataURL reader file)))))

(defn upload-cas-file! [^js file]
  (-> (read-file-as-data-url file)
      (.then (fn [data-url]
               (-> (http/$lambda 'ductile.damages/upload-file {:file data-url})
                   (.then (fn [result]
                            (if (:error result)
                              (js/Promise.reject (ex-info (or (:error-message result)
                                                              (str (:error result)))
                                                          result))
                              (assoc result
                                     :ductile/id (random-uuid)
                                     :file/name (.-name file))))))))))

(defn move-item
  [items from-index to-index]
  (let [item (nth items from-index)]
    (-> items
        (vec)
        (assoc from-index (nth items to-index)
               to-index item))))

(defn cas-files-editor [{:keys [value on-change editing? path diff-status]}]
  (let [path (or path [])
        !input-el (hooks/use-ref nil)
        !dragging? (hooks/use-state false)
        !uploading? (hooks/use-state false)
        !upload-error (hooks/use-state nil)
        editable-cas-files (vec (or (strip-diff-records value) []))
        display-items (->> (cond
                             (mismatch? value)
                             (concat (map diff-impl/->Deletion (or (:- value) []))
                                     (map diff-impl/->Insertion (or (:+ value) [])))

                             (insertion? value)
                             (map diff-impl/->Insertion (or (:+ value) []))

                             (deletion? value)
                             (map diff-impl/->Deletion (or (:- value) []))

                             (sequential? value)
                             value

                             :else [])
                           (map cas-file-diff-item)
                           vec)
        upload-files! (fn [files]
                        (let [files (vec files)]
                          (when (seq files)
                            (reset! !upload-error nil)
                            (reset! !uploading? true)
                            (-> (.all js/Promise (clj->js (map upload-cas-file! files)))
                                (.then (fn [uploaded-cas-files]
                                         (on-change (into editable-cas-files (js->clj uploaded-cas-files :keywordize-keys true)))
                                         (when-let [el @!input-el]
                                           (set! (.-value el) ""))))
                                (.catch (fn [error]
                                          (reset! !upload-error (or (ex-message error)
                                                                    (some-> error ex-data :error-message)
                                                                    (.-message error)
                                                                    (tr "Upload failed")))))
                                (.finally #(reset! !uploading? false))))))
        handle-files! (fn [file-list]
                        (upload-files! (array-seq file-list)))]
    [:div.flex.flex-col.gap-2
     {:class (when editing? "rounded-md border border-slate-200 bg-slate-50/40 p-2")
      :data-testid (str "cas-files-editor-" (pr-str path))}
     (if editing?
       [:<>
        [:div.flex.flex-col.gap-2
         [:input.hidden
          {:type "file"
           :multiple true
           :ref !input-el
           :on-change #(handle-files! (.. % -target -files))}]
         [:button
          {:type "button"
           :data-testid (str "cas-files-upload-" (pr-str path))
           :disabled @!uploading?
           :class (str
                   "rounded-md border border-dashed border-indigo-300 bg-white px-4 py-4 text-left text-sm "
                   "hover:border-indigo-400 hover:bg-indigo-50/50 focus-visible:ring-2 focus-visible:ring-indigo-500 "
                   (when @!dragging? "border-indigo-500 bg-indigo-50 ")
                   (when @!uploading? "cursor-wait opacity-70 "))
           :on-click #(when-let [el @!input-el] (.click el))
           :on-drag-over (fn [e]
                           (.preventDefault e)
                           (.stopPropagation e))
           :on-drag-enter (fn [e]
                            (.preventDefault e)
                            (.stopPropagation e)
                            (reset! !dragging? true))
           :on-drag-leave (fn [e]
                            (.preventDefault e)
                            (.stopPropagation e)
                            (reset! !dragging? false))
           :on-drop (fn [e]
                      (.preventDefault e)
                      (.stopPropagation e)
                      (reset! !dragging? false)
                      (handle-files! (.. e -dataTransfer -files)))}
          [:div.flex.items-center.gap-2.text-indigo-700
           (if @!uploading?
             [ui/loading-indicator]
             [ui/icon :plus])
           [:span.font-medium
            (if @!uploading?
              (str (tr "Uploading documents") "…")
              (tr "Upload documents"))]]
          [:div.mt-1.text-xs.text-slate-500
           (tr "Click to select or drag & drop")]]
         (when @!upload-error
           [:div.rounded-md.border.border-red-200.bg-red-50.p-2.text-xs.text-red-700
            @!upload-error])]
        (if (seq editable-cas-files)
          [:div.flex.flex-col.gap-1
           (doall
            (map-indexed
             (fn [idx cas-file]
               ^{:key (or (:ductile/id cas-file) (:cas/multihash cas-file) (str idx "-" (cas-file-label cas-file)))}
               [:div.flex.items-center.justify-between.gap-3.rounded-md.border.border-slate-200.bg-white.px-3.py-2.text-sm
                {:data-testid (str "cas-files-item-" idx)}
                [:div.min-w-0.flex-1.truncate
                 [cas-file-link cas-file]]
                [:div.flex.items-center.gap-1.text-slate-500
                 [:button
                  {:type "button"
                   :title (tr "Move up")
                   :data-testid (str "cas-files-move-up-" idx)
                   :disabled (or @!uploading? (zero? idx))
                   :class (str "rounded p-1 hover:bg-slate-100 hover:text-indigo-600 "
                               (when (zero? idx) "cursor-default opacity-30"))
                   :on-click #(when (pos? idx)
                                (on-change (move-item editable-cas-files idx (dec idx))))}
                  [ui/icon :triangle-up]]
                 [:button
                  {:type "button"
                   :title (tr "Move down")
                   :data-testid (str "cas-files-move-down-" idx)
                   :disabled (or @!uploading? (>= idx (dec (count editable-cas-files))))
                   :class (str "rounded p-1 hover:bg-slate-100 hover:text-indigo-600 "
                               (when (>= idx (dec (count editable-cas-files))) "cursor-default opacity-30"))
                   :on-click #(when (< idx (dec (count editable-cas-files)))
                                (on-change (move-item editable-cas-files idx (inc idx))))}
                  [ui/icon :triangle-down]]
                 [:button
                  {:type "button"
                   :title (tr "Delete")
                   :data-testid (str "cas-files-delete-" idx)
                   :disabled @!uploading?
                   :class "rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-600"
                   :on-click #(on-change (vec (concat (subvec editable-cas-files 0 idx)
                                                      (subvec editable-cas-files (inc idx)))))}
                  [ui/icon :trash]]]])
             editable-cas-files))]
          [:div.rounded-md.border.border-slate-200.bg-white.px-3.py-2.text-sm.text-slate-500
           (tr "No documents uploaded")])]

       (if (seq display-items)
         [:div.flex.flex-col.gap-1
          (doall
           (map-indexed
            (fn [idx item]
              (cond
                (mismatch? item)
                (let [old-cas-file (:- item)
                      new-cas-file (:+ item)]
                  ^{:key (or (:ductile/id new-cas-file)
                             (:cas/multihash new-cas-file)
                             (:ductile/id old-cas-file)
                             (:cas/multihash old-cas-file)
                             (str "cas-files-changed-" idx))}
                  [:div.flex.items-center.gap-2.text-sm
                   [:span.text-red-500.opacity-70.line-through
                    [cas-file-link old-cas-file {:status :deleted}]]
                   [:span.text-slate-500 [ui/icon {:size 10} :arrow-right]]
                   [:span.text-green-700.font-medium
                    [cas-file-link new-cas-file {:status :inserted}]]])

                :else
                (let [[cas-file item-status] (diff-unwrap item)
                      item-status (or item-status diff-status)]
                  ^{:key (or (:ductile/id cas-file) (:cas/multihash cas-file) (str "cas-files-" idx))}
                  [:div.text-sm
                   {:data-testid (str "cas-files-readonly-" idx)
                    :class (case item-status
                             :inserted "text-green-700 font-medium"
                             :deleted "text-red-500 opacity-70 line-through"
                             nil)}
                   [cas-file-link cas-file {:status item-status}]])))
            display-items))]
         [:span.text-slate-400 (tr "No documents")]))]))

(defn code-editor [{:keys [value on-change schema-properties format-value parse-value]}]
  (let [format-value (or format-value str)
        parse-value (or parse-value identity)
        !ui-value (use-ui-value value)
        display-value (if (some? @!ui-value)
                        @!ui-value
                        (format-value value))]
    [:div {:on-blur (fn [_]
                      (when (some? @!ui-value)
                        (on-change (parse-value @!ui-value))))
           :class "max-w-full [&_.cm-scroller]:!text-[12px] [&_.cm-content]:!text-[12px] [&_.cm-content]:!whitespace-pre-wrap [&_.cm-line]:!break-all [&_.cm-line]:!text-[12px]"}
     [ductile.ui.code/editor {:value display-value
                              :placeholder (when-not (:optional schema-properties) (tr "missing"))
                              :on-change #(reset! !ui-value %)
                              :style :input
                              :allow-tab-out? true}]]))

(defn hover-row-link
  [{:keys [path on-click label class highlight-classes]
    :or {highlight-classes "bg-indigo-50"}}]
  [:span.invisible {:class (str "group-hover/" (dec (count path)) ":visible")}
   [ui/link {:class class
             :on-mouse-down blur-active-input-on-mouse-down!
             :on-mouse-enter #(highlight! "row" path highlight-classes)
             :on-mouse-leave #(unhighlight! "row" path highlight-classes)
             :on-click #(do (unhighlight! "row" path highlight-classes) (on-click))}
    label]])

(defn edit-link [{:keys [path on-click]}]
  [hover-row-link {:path path
                   :on-click on-click
                   :label (tr "Edit")}])

(defn delete-link [{:keys [path on-click]}]
  [hover-row-link {:path path
                   :on-click on-click
                   :label (tr "Delete")
                   :class "text-red-500 hover:text-red-700"
                   :highlight-classes "bg-red-50"}])

(defn readonly-tree-children [child]
  [:div {:class "flex min-w-0 gap-1.5 mt-0.5"}
   [:div {:class "w-2.5 shrink-0 flex justify-center"}
    [:div {:class "w-px self-stretch bg-slate-200/60"}]]
   [:div.min-w-0.flex-1
    child]])

(defn inline-expand-toggle
  [{:keys [expanded? child-path toggle-expanded!]}]
  [:button
   {:type "button"
    :tab-index -1
    :title (tr (if expanded? "Collapse" "Expand"))
    :class "rounded-sm whitespace-nowrap text-[10px] text-slate-400 hover:text-indigo-600 active:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 ring-indigo-500"
    :on-mouse-enter #(highlight! "row" child-path "bg-slate-50")
    :on-mouse-leave #(unhighlight! "row" child-path "bg-slate-50")
    :on-double-click (fn [e]
                       (.preventDefault e)
                       (.stopPropagation e))
    :on-click (fn [e]
                (.preventDefault e)
                (.stopPropagation e)
                (toggle-expanded!))}
   (tr (if expanded? "Collapse" "Expand"))])

(defn map-row-toggle-gutter
  [{:keys [editing? expanded? has-children? reserve-toggle-gutter? editing-root? child-path toggle-expanded!]}]
  (if (and has-children? (not editing-root?) (not editing?))
    [ui/link
     {:on-mouse-enter #(highlight! "row" child-path "bg-slate-50")
      :on-mouse-leave #(unhighlight! "row" child-path "bg-slate-50")
      :on-double-click (fn [e]
                         (.preventDefault e)
                         (.stopPropagation e))
      :on-click (fn [e]
                  (.preventDefault e)
                  (.stopPropagation e)
                  (toggle-expanded!))
      :tab-index -1
      :class (if editing? "gap-0.5" "w-2.5 shrink-0 flex justify-center")}
     [:div
      {:class [(if editing?
                 "size-6 flex items-center justify-center m-1 border leading-none text-indigo-500"
                 "size-4 flex items-center justify-center leading-none text-slate-500")
               "hover:text-indigo-600 active:text-indigo-700"]
       :title (tr (if expanded? "Collapse" "Expand"))}
      [:div.transition-transform.duration-200 {:class (if expanded? "rotate-90" "rotate-0")}
       [ui/icon :chevron-right]]]]
    (when-not editing?
      [:div {:class [(if reserve-toggle-gutter? "w-2.5" "w-1") "shrink-0 flex justify-center"] :aria-hidden true}])))

(defn map-editor-row [{:as opts :keys [attr schema-opts schema-type value on-change path collapsed-by-default? editable-paths editing? editing-path edit! cancel! hover! diff-status reserve-toggle-gutter? required?]}]
  (let [!latest-value (hooks/use-ref value)
        _ (set! (.-current !latest-value) value)
        row-value (get value attr)
        row-diff-status (when-not diff-status
                          (cond
                            (insertion? row-value) :inserted
                            (deletion? row-value) :deleted
                            (mismatch? row-value) :changed))
        child-schema (if (keyword? schema-type)
                       (or (m/deref (m/schema schema-type)) schema-type)
                       schema-type)
        editor-view (or (:editor/view schema-opts)
                        (schema-editor-view child-schema))
        child-path (conj path attr)
        editing-root? (= editing-path child-path)
        editing? (or editing? editing-root?)
        {:keys [child-count has-children? many-children?]}
        (collapsible-meta {:schema child-schema
                           :editor-view editor-view
                           :value row-value
                           :show-count? (and (not (or (:editor/view schema-opts)
                                                      (:editor/view (m/properties child-schema))))
                                             (not= editor-view :map))})
        {:keys [expanded? toggle-expanded!]}
        (use-expanded-state child-path
                            has-children?
                            (and collapsed-by-default? many-children?)
                            (= editing-path path)
                            (or (path-active? child-path editing-path)
                                (path-active? child-path (:active-path opts))))
        in-editable-path? (some #(= % (take (count %) child-path)) editable-paths)
        readonly? (or (:editor/readonly? schema-opts)
                      (and (some? editable-paths) (not in-editable-path?)))
        structural-view (structural-editor-view child-schema)
        structural? (boolean structural-view)
        delete-target-value (edit-target-value row-value)
        has-children-to-delete? (has-children-to-delete? structural-view delete-target-value)
        deletable? (and (not readonly?)
                        (not required?)
                        (not= diff-status :deleted)
                        (not= row-diff-status :deleted))
        editable? (and (not readonly?)
                       (not (and editing? (not editing-root?)))
                       (not= diff-status :deleted)
                       (not= row-diff-status :deleted))
        show-optional-structural-hover-actions? (and editing?
                                                     (not editing-root?)
                                                     structural?
                                                     (not readonly?)
                                                     (not= diff-status :deleted)
                                                     (not= row-diff-status :deleted))
        show-optional-structural-delete? (and show-optional-structural-hover-actions?
                                              deletable?
                                              has-children-to-delete?)
        delete-row! (fn []
                      (-> (flash-delete! child-path)
                          (.then (fn []
                                   (let [current-value (or (.-current !latest-value) {})]
                                     (on-change (preserve-meta current-value
                                                               (dissoc-map-value current-value attr))))))))
        double-click-props (when editable?
                             (edit-on-double-click-props {:edit! edit!
                                                          :editable-paths editable-paths
                                                          :editing? editing?
                                                          :editing-path editing-path
                                                          :schema child-schema
                                                          :value row-value
                                                          :path child-path
                                                          :editor-view editor-view
                                                          :hidden-paths (:hidden-paths opts)}))
        take-over-child-edit? (or (nil? editing-path)
                                  (= editing-path child-path))
        opts' (merge opts
                     {:value (get value attr)
                      :on-change (fn [v]
                                   (let [current-value (or (.-current !latest-value) {})]
                                     (on-change (preserve-meta current-value
                                                               (if (drop-optional-empty-text-field? schema-opts editor-view v)
                                                                 (dissoc-map-value current-value attr)
                                                                 (assoc-map-value current-value attr v))))))
                      :schema schema-type
                      :schema-properties schema-opts
                      :attr attr
                      :collapsed-by-default? true
                      :label (tr attr)
                      :path child-path
                      :editing? editing?
                      :diff-status (or row-diff-status diff-status)}
                     (when (:editor/readonly? schema-opts)
                       {:editable-paths #{} :editing? false :editing-path nil}))
        inline-layout? (and (not editing?) (contains? simple (form-kw child-schema)))
        toggleable? (and has-children? (not editing-root?))
        header-toggleable? (and toggleable? (not editing?))
        label-node [:span
                    {:class ["font-semibold text-slate-900"
                             (when inline-layout? "min-w-[3rem]")]
                     :title (str (pr-str child-path) "\n"
                                 "form-kw " (form-kw child-schema) "\n"
                                 "inline? " (contains? simple (form-kw child-schema)))}
                    [:span (tr (or (:label schema-opts)
                                   (tr attr)))]
                    (when (and editing? required?)
                      [:span.text-orange-500.font-normal {:class "text-[11px]"}
                       (str " (" (tr "required") ")")])]
        collapsible-meta-node [:<>
                               (when (and has-children? child-count (not= editor-view :map))
                                 [:span {:class "text-[10px] text-slate-400 whitespace-nowrap"}
                                  (trn ["%1 entry" "%1 entries"] child-count)])
                               (when (and editing? toggleable?)
                                 [inline-expand-toggle {:expanded? expanded?
                                                        :child-path child-path
                                                        :toggle-expanded! toggle-expanded!}])]
        header-hover-actions (when show-optional-structural-hover-actions?
                               [:span.flex.gap-3.items-center {:class "text-[11px]"}
                                [edit-link {:path child-path
                                            :on-click #(edit! child-path)}]
                                (when show-optional-structural-delete?
                                  [delete-link {:path child-path
                                                :on-click delete-row!}])])
        edit-controls (when (and (or editable? editing-root?)
                                 (or (not has-children?) expanded?))
                        [:span {:class [(when editing? "ml-auto") "text-[11px]"]}
                         (if editing-root?
                           [:span.flex.gap-3.items-center
                            (when deletable?
                              [ui/link
                               {:class "text-red-500 hover:text-red-700"
                                :on-mouse-enter #(highlight! "row" child-path "bg-red-50")
                                :on-mouse-leave #(unhighlight! "row" child-path "bg-red-50")
                                :on-click delete-row!}
                               (tr "Delete")])
                            (when cancel! [ui/link {:on-click cancel!} (tr "Cancel")])
                            [button {:variant :primary :on-click #(edit! nil)} (tr "Done")]]
                           [:span.flex.gap-3.items-center
                            (when-not show-optional-structural-hover-actions?
                              [edit-link {:path child-path
                                          :on-click #(edit! child-path)}])
                            (when (= editor-view :list)
                              [:span.invisible {:class (str "group-hover/" (dec (count child-path)) ":visible")}
                               [ui/link
                                {:on-mouse-down blur-active-input-on-mouse-down!
                                 :on-mouse-enter #(highlight! "row" child-path)
                                 :on-mouse-leave #(unhighlight! "row" child-path)
                                 :on-click (fn []
                                             (unhighlight! "row" child-path)
                                             (let [item-schema (first (m/children child-schema))
                                                   current-list (vec (or row-value []))
                                                   new-index (count current-list)
                                                   new-path (conj child-path new-index)
                                                   new-item (assign-id (apply-auto-id item-schema (default-value item-schema)))
                                                   current-value (or (.-current !latest-value) {})
                                                   focus-path (first-edit-target-path {:schema item-schema
                                                                                       :value new-item
                                                                                       :path new-path
                                                                                       :hidden-paths (:hidden-paths opts)})]
                                               (on-change (preserve-meta current-value
                                                                         (assoc-map-value current-value attr (conj current-list new-item))))
                                               (when (and edit! take-over-child-edit?)
                                                 (edit! new-path)
                                                 (focus-path! focus-path :delay 50))))}
                                (tr "Add")]])])])]
    [:div
     (merge
      {:class [(str "group/" (count path))
               (if editing? edit-form-font-class "text-sm")
               (if editing? "p-1" "py-0.5")
               (when editing-root? "ring-2 ring-indigo-500 rounded p-2")
               (when readonly? "text-slate-700/90")
               (when (and readonly? editing?) "bg-slate-50/30")
               (diff-row-class row-diff-status)]
       :data-row (pr-str child-path)
       :on-mouse-enter (fn [e] (.stopPropagation e) (when hover! (hover! child-path)))}
      double-click-props)

     [:div.flex.items-start.text-xs.min-w-0
      {:class (if editing? "gap-2" "gap-1.5")}
      [map-row-toggle-gutter {:editing? editing?
                              :expanded? expanded?
                              :has-children? has-children?
                              :reserve-toggle-gutter? reserve-toggle-gutter?
                              :editing-root? editing-root?
                              :child-path child-path
                              :toggle-expanded! toggle-expanded!}]
      (if inline-layout?
        [:div {:class "grid flex-1 min-w-0 items-baseline gap-x-2 gap-y-0.5 grid-cols-[minmax(3rem,max-content)_minmax(0,1fr)]"}
         label-node
         [:div {:class "flex items-baseline gap-1.5 min-w-0"}
          [:div.min-w-0 [editor' opts']]
          edit-controls]]
        [:div.flex.flex-1.flex-col.min-w-0
         {:class (when editing? "gap-2")}
         [:div.flex.items-center.gap-2.min-w-0
          [:div.flex.items-center.gap-2.min-w-0
           {:class (when header-toggleable? "cursor-pointer hover:text-indigo-600")
            :on-click (when header-toggleable?
                        (fn [e]
                          (.preventDefault e)
                          (.stopPropagation e)
                          (toggle-expanded!)))
            :on-double-click (when header-toggleable?
                               (fn [e]
                                 (.preventDefault e)
                                 (.stopPropagation e)))}
           label-node
           collapsible-meta-node
           header-hover-actions]
          edit-controls]

         (when (and expanded? editing? (:description schema-opts))
           [:span.text-slate-500.text-xs
            (tr (:description schema-opts))])

         (when expanded?
           (if editing?
             [:div.min-w-0
              [editor' opts']]
             [:div.min-w-0.-ml-4
              [readonly-tree-children
               [editor' opts']]]))])]]))

(defn map-add-field
  "Combobox to add a missing field to a map. Only rendered when not in edit mode."
  [{:keys [missing-fields value on-change path edit! reveal-demoted! reserve-toggle-gutter?]}]
  (when (seq missing-fields)
    [:div.pt-1 {:class ["max-w-[10rem]" (when reserve-toggle-gutter? "ml-4")]}
     [combobox/view
      {:value nil
       :placeholder (tr "Add field")
       :on-change (fn [{attr :ui/value}]
                    (let [[_ field-schema-opts field-schema] (first (filter #(= (first %) attr) missing-fields))
                          child-path (conj path attr)
                          initial-value (if (and (:optional field-schema-opts)
                                                 (structural-editor-view field-schema))
                                          nil
                                          (default-value field-schema))
                          next-value (if (contains? value attr)
                                       value
                                       (assoc-map-value value attr initial-value))]
                      (on-change (preserve-meta value next-value))
                      (when (and reveal-demoted! (:editor/demoted? field-schema-opts))
                        (reveal-demoted! child-path))
                      (edit! child-path)
                      (focus-path! child-path)))
       :choices (mapv (fn [[attr schema-opts _]]
                        {:ui/label (tr attr)
                         :ui/description (some-> (:description schema-opts) display-label)
                         :ui/value attr})
                      missing-fields)}]]))

(defn map-editor [{:as opts :keys [value schema editing? path on-change edit! hidden-attrs hidden-paths suppress-promoted-label? revealed-demoted-paths schema-properties]}]
  (let [missing-optional-map? (and editing?
                                   (:optional schema-properties)
                                   (nil? value))
        children (m/children schema)
        path (vec (or path []))
        value (normalize-diff-map-keys (or value {}))
        hidden-paths (into (set (or hidden-paths #{}))
                           (map #(conj path %))
                           (or hidden-attrs #{}))
        render-hidden-paths (into hidden-paths
                                  (map #(conj path %))
                                  (when (and suppress-promoted-label? (not editing?))
                                    (some-> (promoted-label-attr (schema-label-key schema) value)
                                            vector)))
        hidden? (fn [attr schema-opts] (or (:editor/hidden? schema-opts)
                                           (contains? render-hidden-paths (conj path attr))))
        demoted-hidden? (fn [attr schema-opts]
                          (and (:editor/demoted? schema-opts)
                               (not (contains? revealed-demoted-paths (conj path attr)))))
        visible-children (filterv (fn [[attr schema-opts _]]
                                    (and (not (hidden? attr schema-opts))
                                         (not (demoted-hidden? attr schema-opts))
                                         (or editing? (contains? value attr))))
                                  children)
        reserve-toggle-gutter? (map-reserve-toggle-gutter? {:schema schema
                                                            :value value
                                                            :path path
                                                            :hidden-attrs hidden-attrs
                                                            :hidden-paths hidden-paths
                                                            :suppress-promoted-label? suppress-promoted-label?
                                                            :editing? editing?})
        missing-fields (filterv (fn [[attr schema-opts _]] (and (or (not (contains? value attr))
                                                                    (demoted-hidden? attr schema-opts))
                                                                (not (:editor/readonly? schema-opts))
                                                                (not (hidden? attr schema-opts)))) children)]
    (if missing-optional-map?
      (let [new-value {}
            focus-path (first-edit-target-path {:schema schema
                                                :value new-value
                                                :path path
                                                :hidden-paths hidden-paths})
            display-label (or (some-> (:label schema-properties) tr)
                              (some-> (last path) tr)
                              (schema-label schema)
                              (tr "field"))]
        [:div.flex.flex-col.w-full
         [add-button
          {:on-click (fn []
                       (on-change new-value)
                       (focus-path! focus-path :delay 50))
           :class "border"}
          (tr "Add %1" display-label)]])
      [:div.flex.flex-col.w-full
       {:class [(str "group/map-" (count path))
                (if editing? "rounded" "gap-0.5")]}
       (cond-> (into [:<>]
                     (map (fn [[attr schema-opts schema-type]]
                            ^{:key attr}
                            [map-editor-row (merge opts {:attr attr
                                                         :hidden-paths hidden-paths
                                                         :schema-opts schema-opts
                                                         :schema-type schema-type
                                                         :value value
                                                         :required? (not (:optional schema-opts))
                                                         :reserve-toggle-gutter? reserve-toggle-gutter?})]))
                     visible-children)
         (and (seq (:editable-paths opts)) (not (seq path)) (seq missing-fields) (not editing?))
         (conj ^{:key ::add-field}
          [map-add-field {:missing-fields missing-fields
                          :value value
                          :on-change on-change
                          :path path
                          :edit! edit!
                          :reveal-demoted! (:reveal-demoted! opts)
                          :reserve-toggle-gutter? reserve-toggle-gutter?}]))])))

(defn add-button [props label]
  [:button.border.rounded.p-1.self-start.outline-none.appearance-none.text-center.text-indigo-500.text-xs.focus-visible:ring-2.focus-visible:ring-indigo-500.hover:bg-slate-50.hover:text-indigo-600.active:bg-slate-100
   (merge {:type "button"} props)
   [:div.flex.gap-2.items-center
    [ui/icon :plus]
    label]])

(defn insert-line [editing?]
  ["relative" "after:content-['']" "after:absolute" "after:inset-x-0" "after:h-0.5" "after:bg-indigo-400"
   (if editing? "after:bottom-[-6px]" "after:bottom-0")])

(defn structured-list-item
  [{:keys [opts item-key item-value child-schema child-view child-path item-diff-status editable? edit-actions
           on-change-item promoted-label-key index diff-status editing-root? reserve-toggle-gutter?]}]
  (let [editing-path (:editing-path opts)
        editing? (:editing opts)
        hover! (:hover! opts)
        click! (:click! opts)
        root-schema (:root-schema opts)
        root-value (:root-value opts)
        {:keys [child-count has-children? many-children?]}
        (collapsible-meta {:schema child-schema
                           :editor-view child-view
                           :value (if (diff-record? item-value)
                                    (first (diff-unwrap item-value))
                                    item-value)
                           :show-count? (and (not editing?) (not= child-view :map))})
        {:keys [expanded? toggle-expanded!]}
        (use-expanded-state child-path
                            has-children?
                            (and (not editing?) (:collapsed-by-default? opts) many-children?)
                            (= editing-path (:path opts))
                            (or (path-active? child-path editing-path)
                                (path-active? child-path (:active-path opts))))
        expanded? (or editing? expanded?)
        promoted-heading? (and (not editing?)
                               promoted-label-key
                               (some? (label-key-value promoted-label-key item-value)))
        edit-header-preview-label (or (some-> (when promoted-label-key
                                                (label-key-value promoted-label-key item-value))
                                              breadcrumb-label-text)
                                      (schema-preview-label child-schema item-value))
        readonly-heading (when-not editing?
                           (or (when promoted-heading?
                                 [simple-readonly-value {:value item-value
                                                         :schema-properties {:label-key promoted-label-key}}])
                               (tr "Item %1" (inc index))))
        double-click-props (when editable?
                             (edit-on-double-click-props {:edit! (:edit! opts)
                                                          :editable-paths (:editable-paths opts)
                                                          :editing? (:editing opts)
                                                          :editing-path editing-path
                                                          :schema child-schema
                                                          :value item-value
                                                          :path child-path
                                                          :editor-view child-view
                                                          :preferred-path (when promoted-heading?
                                                                            (promoted-label-path child-path promoted-label-key item-value))
                                                          :hidden-paths (:hidden-paths opts)}))]
    [:div
     (merge
      {:key item-key
       :class [(:class opts)
               (if editing?
                 "rounded border"
                 "flex flex-col gap-0.5 py-0.5")
               (when editing-root? "ring-2 ring-indigo-500 rounded")
               (case item-diff-status
                 :inserted "text-green-700 font-medium"
                 :deleted  "text-red-500 font-medium opacity-70 line-through hover:no-underline"
                 nil)]
       :data-row (pr-str child-path)
       :on-mouse-enter (fn [e]
                         (.stopPropagation e)
                         (when hover! (hover! child-path)))
       :on-click (fn [_]
                   (when click! (click! child-path)))}
      double-click-props)

     (when (or editing?
               readonly-heading
               (and editable?
                    (not= item-diff-status :deleted)
                    (not= diff-status :deleted)))
       [:div
        {:class [(if editing?
                   "bg-slate-50 rounded-t border-b border-slate-200 px-2.5 py-1 text-[10px]"
                   "text-[10px] leading-4 text-slate-400")
                 (when editing-root? "sticky bg-white")]
         :style (when editing-root? {:top (:sticky-top-offset opts "0rem") :z-index 19})}
        (if editing?
          [:div.flex.items-center.justify-between.gap-2
           [:div {:class "flex items-center gap-1.5 min-w-0"}
            [:span.text-slate-400.flex.gap-1
             [breadcrumbs {:path child-path
                           :schema root-schema
                           :value root-value
                           :last-segment-label edit-header-preview-label}]]]
           (when (and editable?
                      (not= item-diff-status :deleted)
                      (not= diff-status :deleted))
             edit-actions)]
          [:div {:class "flex items-start gap-1.5 min-w-0"}
           [map-row-toggle-gutter {:editing? editing?
                                   :expanded? expanded?
                                   :has-children? has-children?
                                   :reserve-toggle-gutter? reserve-toggle-gutter?
                                   :editing-root? false
                                   :child-path child-path
                                   :toggle-expanded! toggle-expanded!}]
           [:div {:class "flex items-center gap-1.5 min-w-0"}
            [:div
             {:class ["flex items-center gap-1.5 min-w-0"
                      (when has-children? "cursor-pointer hover:text-indigo-600")]
              :on-click (when has-children?
                          (fn [e]
                            (.preventDefault e)
                            (.stopPropagation e)
                            (toggle-expanded!)))
              :on-double-click (when has-children?
                                 (fn [e]
                                   (.preventDefault e)
                                   (.stopPropagation e)))}
             [:span {:class "min-w-0 truncate text-[13px] font-semibold text-slate-900"}
              readonly-heading]
             (when (and has-children? child-count)
               [:span {:class "text-[10px] text-slate-400 whitespace-nowrap"}
                (trn ["%1 entry" "%1 entries"] child-count)])]]
           (when (and editable?
                      (not= item-diff-status :deleted)
                      (not= diff-status :deleted))
             edit-actions)])])

     (when expanded?
       (if editing?
         [:div {:class (if (= child-view :map) "px-1.5" "p-1.5")}
          [:div.flex-grow.flex.items-center.self-stretch.min-w-0
           [editor' (merge opts {:value item-value
                                 :editing? editing?
                                 :on-change on-change-item
                                 :schema child-schema
                                 :schema-properties nil
                                 :suppress-promoted-label? promoted-heading?
                                 :collapsed-by-default? true
                                 :path child-path})]]]
         [readonly-tree-children
          [:div.flex-grow.flex.items-center.self-stretch.min-w-0
           [editor' (merge opts {:value item-value
                                 :editing? editing?
                                 :on-change on-change-item
                                 :schema child-schema
                                 :schema-properties nil
                                 :suppress-promoted-label? promoted-heading?
                                 :collapsed-by-default? true
                                 :path child-path})]]]))]))

(defn list-editor
  "Generic list editor for :vector, :sequential, :set, and also used by :cat sections."
  [{:as opts :keys [value on-change child-schema min-count add-label allow-delete? allow-reorder? path edit! editable-paths editing-path editing? diff-status]
    :or {allow-delete? true allow-reorder? true}}]
  (let [value (or value [])
        path (or path [])
        !latest-value (hooks/use-ref value)
        list-root-editing? editing?
        inline? (contains? simple (form-kw child-schema))
        child-view (schema-editor-view child-schema)
        promoted-label-key (schema-label-key child-schema)
        min-count (or min-count 0)
        can-delete? (and allow-delete? (<= min-count (count value)))
        has-diff? (some diff-record? value)
        editing-branch? (path-active? path editing-path)
        list-branch-editing? (or list-root-editing? editing-branch?)
        take-over-list-edit? (or (nil? editing-path)
                                 (= editing-path path))
        value (if list-branch-editing? (strip-diff-records value) value)
        reserve-toggle-gutter? (and (not list-root-editing?)
                                    (not inline?)
                                    (some #(has-collapsible-content? child-schema
                                                                     child-view
                                                                     (if (diff-record? %)
                                                                       (first (diff-unwrap %))
                                                                       %))
                                          value))
        group-id (count path)
        _ (set! (.-current !latest-value) value)
        with-latest-value! (fn [f]
                             (-> (flush-active-input!)
                                 (.then (fn []
                                          (f (or (.-current !latest-value) []))))))
        item-key (fn [i v] (or (::id (meta v)) i))]
    (conj
     (into [:div.flex.flex-col {:class (when list-branch-editing? "gap-1.5")}]
           (map-indexed
            (fn [i item-value]
              (let [item-diff-status (cond
                                       (insertion? item-value) :inserted
                                       (deletion? item-value)  :deleted
                                       (mismatch? item-value)  :changed)
                    child-path (conj path i)
                    editing-root? (= editing-path child-path)
                    implicit-empty-child? (and list-root-editing?
                                               (nil? item-value)
                                               (not editing-root?))
                    editing? (or (and list-root-editing?
                                      (not implicit-empty-child?))
                                 editing-root?)

                    editing-child? (and editing? (not editing-root?))
                    readonly? (:editor/readonly? (:schema-properties opts))
                    editable? (and (not readonly?)
                                   (not= item-diff-status :deleted)
                                   (some #(= % (take (count %) child-path)) editable-paths))
                    opts (-> opts
                             (assoc :editing editing?)
                             (cond-> item-diff-status (assoc :diff-status item-diff-status)
                               readonly? (assoc :editable-paths #{}))
                             (dissoc :allow-delete?))
                    edit-actions [:span.flex.gap-3.items-center {:class "text-[11px]"}
                                  (if editing?
                                    [:<>
                                     (when can-delete?
                                       [ui/link
                                        {:class "text-red-500 hover:text-red-700"
                                         :on-mouse-down blur-active-input-on-mouse-down!
                                         :on-mouse-enter #(highlight! "row" child-path "bg-red-50")
                                         :on-mouse-leave #(unhighlight! "row" child-path "bg-red-50")
                                         :on-click (fn []
                                                     (unhighlight! "row" child-path "bg-red-50")
                                                     (with-latest-value!
                                                       (fn [current-value]
                                                         (-> (flash-delete! (conj path i))
                                                             (.then (fn []
                                                                      (when editing-root? (edit! nil))
                                                                      (on-change (vec (concat (subvec current-value 0 i)
                                                                                              (subvec current-value (inc i)))))))))))}
                                        (tr "Delete")])
                                     (when allow-reorder?
                                       (let [classes (conj (insert-line editing?) "bg-indigo-50")]
                                         [ui/link
                                          {:on-mouse-down blur-active-input-on-mouse-down!
                                           :on-mouse-enter #(highlight! "row" child-path classes)
                                           :on-mouse-leave #(unhighlight! "row" child-path classes)
                                           :on-click (fn []
                                                       (unhighlight! "row" child-path classes)
                                                       (with-latest-value!
                                                         (fn [current-value]
                                                           (let [new-index (inc i)]
                                                             (when editing-root?
                                                               (edit! (update child-path (dec (count child-path)) inc)))
                                                             (on-change (vec (concat (subvec current-value 0 new-index)
                                                                                     [(assign-id (apply-auto-id child-schema (nth current-value i)))]
                                                                                     (subvec current-value new-index))))
                                                             (focus-path! (conj path new-index) :delay 50)))))}
                                          (tr "Duplicate")]))
                                     (when allow-reorder?
                                       (let [first? (zero? i)
                                             last? (>= i (dec (count value)))]
                                         [:span.flex.items-center
                                          [:span {:class (if first? "text-slate-300 cursor-default" "cursor-pointer hover:text-indigo-600")
                                                  :on-mouse-down (when-not first? blur-active-input-on-mouse-down!)
                                                  :on-click (when-not first?
                                                              (fn []
                                                                (with-latest-value!
                                                                  (fn [current-value]
                                                                    (let [new-value (assoc current-value i (nth current-value (dec i)) (dec i) (nth current-value i))]
                                                                      (when editing-root?
                                                                        (edit! (update child-path (dec (count child-path)) dec)))
                                                                      (on-change new-value))))))}
                                           [ui/icon :triangle-up]]
                                          [:span {:class (if last? "text-slate-300 cursor-default" "cursor-pointer hover:text-indigo-600")
                                                  :on-mouse-down (when-not last? blur-active-input-on-mouse-down!)
                                                  :on-click (when-not last?
                                                              (fn []
                                                                (with-latest-value!
                                                                  (fn [current-value]
                                                                    (let [new-value (assoc current-value i (nth current-value (inc i)) (inc i) (nth current-value i))]
                                                                      (when editing-root?
                                                                        (edit! (update child-path (dec (count child-path)) inc)))
                                                                      (on-change new-value))))))}
                                           [ui/icon :triangle-down]]]))
                                     (when editing-root?
                                       [:<>
                                        (when (:cancel! opts) [ui/link {:on-click (:cancel! opts)} (tr "Cancel")])
                                        [button
                                         {:on-click #(edit! nil)
                                          :variant :primary}
                                         (tr "Done")]])]

                                    [:<>
                                     [edit-link {:path child-path
                                                 :on-click #(edit! child-path)}]
                                     [:span.invisible {:class (str "group-hover/" group-id ":visible")}
                                      [ui/link {:on-mouse-down blur-active-input-on-mouse-down!
                                                :on-mouse-enter #(highlight! "row" child-path (insert-line editing?))
                                                :on-mouse-leave #(unhighlight! "row" child-path (insert-line editing?))
                                                :on-click (fn []
                                                            (unhighlight! "row" child-path (insert-line editing?))
                                                            (with-latest-value!
                                                              (fn [current-value]
                                                                (let [new-index (inc i)
                                                                      new-path (conj path new-index)
                                                                      new-item (assign-id (apply-auto-id child-schema (default-value child-schema)))
                                                                      focus-path (first-edit-target-path {:schema child-schema
                                                                                                          :value new-item
                                                                                                          :path new-path
                                                                                                          :hidden-paths (:hidden-paths opts)})]
                                                                  (on-change (vec (concat (subvec current-value 0 new-index)
                                                                                          [new-item]
                                                                                          (subvec current-value new-index))))
                                                                  (when (and edit! take-over-list-edit?)
                                                                    (edit! new-path)
                                                                    (when-not has-diff? (focus-path! focus-path :delay 50)))))))}
                                       (tr "Add")]]])]]
                (if inline?
                  [:div.flex.items-center
                   {:key (item-key i item-value)
                    :class [(str "group/" group-id)
                            (if list-branch-editing? "gap-2" "gap-3")
                            "min-w-0"]
                    :data-row (pr-str child-path)}
                   [editor' (merge opts {:value item-value
                                         :on-change (fn [v] (on-change (replace-index (or (.-current !latest-value) []) i v)))
                                         :schema child-schema
                                         :schema-properties nil
                                         :path child-path})]
                   (when (and editable? (not= diff-status :deleted)) edit-actions)]

                  [structured-list-item
                   {:opts (assoc opts :class (str "group/" group-id))
                    :item-key (item-key i item-value)
                    :item-value item-value
                    :child-schema child-schema
                    :child-view child-view
                    :child-path child-path
                    :item-diff-status item-diff-status
                    :editable? editable?
                    :edit-actions edit-actions
                    :promoted-label-key promoted-label-key
                    :index i
                    :diff-status diff-status
                    :editing-root? editing-root?
                    :reserve-toggle-gutter? reserve-toggle-gutter?
                    :on-change-item (fn [v] (on-change (replace-index (or (.-current !latest-value) []) i v)))}])))
            value))
     (when list-branch-editing?
       (let [last-path (when (seq value) (conj path (dec (count value))))]
         [add-button
          {:on-mouse-down blur-active-input-on-mouse-down!
           :on-mouse-enter #(when last-path (highlight! "row" last-path (insert-line editing?)))
           :on-mouse-leave #(when last-path (unhighlight! "row" last-path (insert-line editing?)))
           :on-click (fn []
                       (when last-path (unhighlight! "row" last-path (insert-line editing?)))
                       (with-latest-value!
                         (fn [current-value]
                           (let [new-index (count current-value)
                                 new-path (conj path new-index)
                                 new-item (assign-id (apply-auto-id child-schema (default-value child-schema)))
                                 focus-path (first-edit-target-path {:schema child-schema
                                                                     :value new-item
                                                                     :path new-path
                                                                     :hidden-paths (:hidden-paths opts)})]
                             (on-change (conj current-value new-item))
                             (when (and edit! take-over-list-edit?)
                               (edit! new-path)
                               (when-not has-diff? (focus-path! focus-path :delay 50)))))))}
          (or add-label
              (let [form (m/form child-schema)]
                (if (keyword? form)
                  (tr "Add %1" (tr form))
                  (tr "Add item"))))])))))

(defn optional-editor
  "Editor for :? (optional) schema - shows add button if empty, editor + delete if present."
  [{:as opts :keys [value on-change inner-schema path editing? schema-properties]}]
  (let [has-value? (seq value)
        path (or path [])
        child-path (conj path 0)
        child-value (first value)
        {:keys [label description]} schema-properties
        child-structural? (contains? structural-views (schema-editor-view inner-schema))
        row-schema-opts (assoc (merge (m/properties (resolved-schema inner-schema))
                                      schema-properties)
                               :label (or (some-> label tr)
                                          (schema-label inner-schema)))
        display-label (or (some-> label tr)
                          (schema-label inner-schema))]
    [:div.flex.flex-col
     {:class (if editing? "gap-1" "gap-0.5")}
     (when (and editing? description (not child-structural?))
       [:div.text-slate-500.text-xs.mb-1 (tr description)])
     (if has-value?
       [map-editor-row (merge opts {:attr 0
                                    :value {0 child-value}
                                    :on-change #(on-change (if (contains? % 0)
                                                             [(get % 0)]
                                                             []))
                                    :schema-opts row-schema-opts
                                    :schema-type inner-schema
                                    :required? false
                                    :path path})]
       (when editing?
         [add-button
          {:on-click (fn []
                       (on-change [(assign-id (default-value inner-schema))])
                       (focus-path! child-path))
           :class "border"}
          (or display-label
              (let [form (m/form inner-schema)]
                (if (keyword? form)
                  (tr "Add optional %1" (tr form))
                  (tr "Add optional"))))]))]))

(defn cat-editor
  "Editor for catenation schemas with support for `:+, :*, :?`
   Internally uses nested structure, flattens on change.
   Stores nested structure in local state to preserve section ownership."
  [{:as opts :keys [value on-change schema path hover! editing? structural-diff-value schema-properties]}]
  (let [child-schemas (m/children schema)
        path (or path [])
        optional? (:optional schema-properties)
        source-value (if (and (not editing?) (diff-record? structural-diff-value))
                       structural-diff-value
                       value)
        !nested (hooks/use-state (if editing?
                                   (cat-edit-sections child-schemas value optional?)
                                   (cat-sections child-schemas source-value)))
        !last-emitted (hooks/use-ref nil)
        nested @!nested
        on-section-change (fn [section-index new-section-value]
                            (let [new-nested (assoc nested section-index new-section-value)
                                  flat (vec (apply concat new-nested))]
                              (reset! !nested new-nested)
                              (reset! !last-emitted flat)
                              (on-change flat)))
        ;; Cumulative offsets per section for flattening hover paths
        section-offsets (vec (reductions + 0 (map count nested)))
        depth (count path)
        flat-hover! (when hover!
                      (fn [child-path]
                        (let [child-path (vec child-path)]
                          (if (and (> (count child-path) (inc depth))
                                   (number? (get child-path depth))
                                   (number? (get child-path (inc depth))))
                            (let [section-idx (get child-path depth)
                                  item-idx (get child-path (inc depth))
                                  flat-idx (+ (nth section-offsets section-idx 0) item-idx)]
                              (hover! (into (subvec child-path 0 depth)
                                            (cons flat-idx (subvec child-path (+ depth 2))))))
                            (hover! child-path)))))
        opts (cond-> opts flat-hover! (assoc :hover! flat-hover!))]

    ;; Only re-decatenate when value changed externally (not from our own on-section-change)
    (hooks/use-effect
     (fn []
       (when (not= value @!last-emitted)
         (reset! !nested (if editing?
                           (cat-edit-sections child-schemas value optional?)
                           (cat-sections child-schemas source-value)))))
     [value structural-diff-value editing? optional?])

    [:div
     (into [:ul.flex.flex-col {:class (when editing? "gap-2")}]
           (map-indexed
            (fn [section-index child-schema]
              (let [section-value (nth nested section-index [])
                    inner-schema (when (#{:+ :* :?} (m/type child-schema))
                                   (first (m/children child-schema)))
                    schema-type (m/type child-schema)
                    section-path (conj path section-index)]
                [:li {:key section-index}
                 (case schema-type
                   ;; :+ (one or more) - use list-editor with min-count 1
                   :+ [list-editor (merge opts {:value section-value
                                                :on-change #(on-section-change section-index %)
                                                :child-schema inner-schema
                                                :min-count 1
                                                :allow-delete? (> (count section-value) 1)
                                                :path section-path})]

                   ;; :* (zero or more) - use list-editor with min-count 0
                   :* [list-editor (merge opts {:value section-value
                                                :on-change #(on-section-change section-index %)
                                                :child-schema inner-schema
                                                :min-count 0
                                                :path section-path})]

                   ;; :? (optional)
                   :? [optional-editor (merge opts {:value section-value
                                                    :on-change #(on-section-change section-index %)
                                                    :inner-schema inner-schema
                                                    :schema-properties (m/properties child-schema)
                                                    :path section-path})]

                   ;; Default: single required value
                   [editor' (merge opts {:value (first section-value)
                                         :on-change #(on-section-change section-index [%])
                                         :schema child-schema
                                         :path section-path})])]))
            child-schemas))]))

(defn map-of-editor
  "Editor for :map-of schemas. Stores entries as vector of {:key k :value v} maps.
   No deduplication during editing - use coerce-value to finalize."
  [{:as opts :keys [value schema path]}]
  (let [[key-schema value-schema] (m/children schema)
        child-schema [:map [:key key-schema] [:value value-schema]]
        ;; Convert map to vector of entries, or use as-is if already a vector
        entries (if (map? value)
                  (vec (map (fn [[k v]] {:key k :value v}) value))
                  (vec value))]
    [list-editor (merge opts {:value entries
                              :child-schema child-schema})]))

(defn set-checkbox-editor
  "Render a set of enums as checkboxes."
  [{:keys [value on-change options collection-kind]}]
  (let [plain (strip-diff-records (or value (case collection-kind
                                              :set #{}
                                              [])))
        selected-options (set plain)]
    [:div.grid.grid-cols-2.gap-1
     (for [option options]
       ^{:key option}
       [:div.min-w-0
        [checkbox
         {:checked (contains? selected-options option)
          :on-change (fn [e]
                       (let [checked (.. e -target -checked)]
                         (on-change
                          (case collection-kind
                            :set
                            (if checked
                              (conj selected-options option)
                              (disj selected-options option))

                            (let [plain (vec plain)]
                              (if checked
                                (cond-> plain
                                  (not (contains? selected-options option))
                                  (conj option))
                                (into [] (remove #{option}) plain)))))))}
         (tr option)]])]))

(defn multi-editor [{:as opts :keys [value on-change schema path editing?]}]
  (let [branches (vec (m/children schema))
        dispatch-fn (:dispatch (second (m/form schema)))
        path (or path [])
        plain-value (strip-diff-records (if (deletion? value) (:- value) value))
        dispatch-value (when dispatch-fn (dispatch-fn plain-value))
        matching-index (or (first (keep-indexed
                                   (fn [i [branch-dispatch-value]]
                                     (when (= branch-dispatch-value dispatch-value) i))
                                   branches))
                           (when (nil? dispatch-value)
                             (first (keep-indexed
                                     (fn [i [branch-dispatch-value]]
                                       (when (nil? branch-dispatch-value) i))
                                     branches))))
        !selected-index (hooks/use-state matching-index)
        active-branch (when @!selected-index
                        (nth branches @!selected-index nil))
        [_ _ active-schema] active-branch
        select-schema! (fn [index]
                         (let [[_ _ child-schema] (nth branches index)
                               current-el js/document.activeElement
                               new-val (default-value child-schema)]
                           (reset! !selected-index index)
                           (on-change new-val)
                           (js/requestAnimationFrame
                            #(focus-path! path :after-el current-el))))]

    (hooks/use-effect (fn [] (when matching-index (reset! !selected-index matching-index))) [matching-index])

    [:div
     (when editing?
       [:div.pb-2
        [combobox/view
         {:value @!selected-index
          :placeholder (tr "Select type")
          :width :xl
          :on-change #(select-schema! (:ui/value %))
          :choices (into []
                         (map-indexed
                          (fn [i [_ _ child-schema]]
                            {:ui/label (schema-label child-schema)
                             :ui/value i}))
                         branches)}]])
     (when active-schema
       [editor' (merge opts {:value (if (nil? value)
                                      (default-value active-schema)
                                      value)
                             :on-change on-change
                             :schema active-schema
                             :schema-properties nil
                             :path path})])]))

(defn or-editor [{:as opts :keys [value on-change schema schema-properties path editing? structural-diff-value]}]
  (let [or-children (vec (m/children schema))
        path (or path [])
        label-key (or (:label-key schema-properties)
                      (schema-label-key schema))
        all-maps? (every? #(= :map (m/type %)) or-children)
        ;; Deep-strip diff records for schema matching (values may contain nested Mismatches)
        ;; For Deletions, use the old value (:- ) so we can match the correct schema type
        plain-value (strip-diff-records (if (deletion? value) (:- value) value))
        matching-index (or-matching-index or-children plain-value)
        !selected-index (hooks/use-state matching-index)

        active-schema (when @!selected-index (nth or-children @!selected-index nil))
        active-value (if (and (not editing?) (diff-record? structural-diff-value))
                       (let [old-value (diff-record-old-value structural-diff-value)
                             new-value (diff-record-new-value structural-diff-value)]
                         (cond
                           (and (some? old-value) (some? new-value))
                           (or (ddiff/diff old-value new-value) new-value)

                           (some? new-value)
                           (diff-impl/->Insertion new-value)

                           (some? old-value)
                           (diff-impl/->Deletion old-value)

                           :else value))
                       value)
        promoted-label-attr (when-not editing?
                              (promoted-label-attr label-key plain-value))
        reserve-toggle-gutter? (and all-maps?
                                    (map-reserve-toggle-gutter? {:schema active-schema
                                                                 :value (strip-diff-records active-value)
                                                                 :path path
                                                                 :hidden-attrs (when promoted-label-attr [promoted-label-attr])
                                                                 :hidden-paths (:hidden-paths opts)
                                                                 :editing? editing?}))

        select-schema! (fn [selected-schema]
                         (let [index (first (keep-indexed #(when (= %2 selected-schema) %1) or-children))
                               current-el js/document.activeElement
                               base-val (default-value selected-schema)
                               merged-val (cond-> base-val
                                            label-key
                                            (merge (select-keys plain-value (label-key-attrs label-key))))
                               new-val (if (map? merged-val)
                                         (-> (preserve-meta plain-value merged-val)
                                             (vary-meta assoc ::or-index index))
                                         merged-val)]
                           (js/requestAnimationFrame
                            (fn []
                              (reset! !selected-index index)
                              (on-change new-val)
                              ;; disabled for now because it causes the or-editor type combobox to be focused ("keeps staying open")
                              #_(when-not (editable-label-key-attr label-key new-val)
                                  (js/requestAnimationFrame
                                   #(focus-path! path :after-el current-el)))))))

        type-select (if editing?
                      [:div.pb-2
                       [combobox/view
                        {:value active-schema
                         :placeholder (tr "Select type")
                         :size 1
                         :width :xl
                         :on-change #(select-schema! (:ui/value %))
                         :choices (into []
                                        (map
                                         (fn [child-schema]
                                           {:ui/label (schema-label child-schema)
                                            :ui/description (some-> child-schema m/properties :description display-label)
                                            :ui/value child-schema}))
                                        or-children)}]]
                      (let [always-label (some-> active-schema m/properties :editor/always-visible-label display-label)]
                        (when (or all-maps? always-label) ;; :or with non-maps don't show type
                          [:span.text-xs
                           (cond
                             always-label always-label
                             active-schema (schema-label active-schema)
                             :else (simple-readonly-value nil))])))]

    ;; Sync selected index when value/schema match changes (e.g. toggling diff mode)
    (hooks/use-effect (fn [] (when matching-index (reset! !selected-index matching-index))) [matching-index])

    [:div
     (when (and promoted-label-attr
                (not (:suppress-promoted-label? opts)))
       [map-editor-row (merge opts {:attr promoted-label-attr
                                    :value plain-value
                                    :schema-opts nil
                                    :schema-type :string+
                                    :required? false
                                    :reserve-toggle-gutter? reserve-toggle-gutter?})])
     (let [type-row (when all-maps?
                      (let [label->description (into {}
                                                     (keep (fn [s]
                                                             (when-let [d (some-> s m/properties :description display-label)]
                                                               [(schema-label s) d])))
                                                     or-children)]
                        [map-editor-row (merge opts
                                               {:attr ::type
                                                :value {::type (schema-label active-schema)}
                                                :schema-opts {:description-key label->description}
                                                :schema-type (into [:enum] (map schema-label) or-children)
                                                :required? true
                                                :reserve-toggle-gutter? reserve-toggle-gutter?
                                                :on-change
                                                (fn [{::keys [type]}]
                                                  (select-schema!
                                                   (first (filter #(= type (schema-label %)) or-children))))})]))
           active-editor (when active-schema
                           [editor' (merge opts {:value active-value
                                                 :on-change on-change
                                                 :schema active-schema
                                                 :schema-properties nil
                                                 :hidden-attrs (when promoted-label-attr [promoted-label-attr])
                                                 :suppress-nested-readonly? (and (not editing?) all-maps?)
                                                 :path path})])]
       (if (and (not editing?) all-maps?)
         [:div
          type-row
          active-editor]
         [:<>
          (if all-maps? type-row type-select)
          active-editor]))]))

(defn- location-filter-fn [{:keys [search-text search-by-dealer-code?]}]
  {:search-params
   (cond-> {}
     (and search-by-dealer-code? search-text)
     (assoc :external.bmw/location-codes {:ductile.entity-search/regex search-text})
     (and (not search-by-dealer-code?) (some-> search-text parse-long))
     (assoc :address/postcode {:ductile.entity-search/regex search-text})
     (and (not search-by-dealer-code?) search-text (not (some-> search-text parse-long)))
     (assoc :address/name {:ductile.entity-search/regex (str "(?i)" search-text)}))})

(defn location-description [loc]
  (->> [(:address/name loc)
        (:address/street loc)
        (:address/postcode loc)
        (:address/city loc)]
       (remove str/blank?)
       (str/join ", ")))

(defn location-editor [{:as opts :keys [value on-change editing? path]}]
  (if-not editing?
    [simple-readonly-value {:value (:address/description value)}]
    (let [component-key (conj (or path []) :location-autocomplete)
          clean-value (strip-diff-records value)]
      [ui/locations-autocomplete
       {:component-key component-key
        :filter (fn [params]
                  (deframe/call-lambda
                   {:result-path (ui/locations-autocomplete-path component-key [:filtered-items])}
                   'ductile.locations/search*
                   (location-filter-fn params)))
        :input-placeholder (str (tr "Type to filter") "…")
        :button-placeholder [:div.flex.items-center.gap-2
                             (str (tr "Click to select") "…")
                             [ui/icon {:size 12} :map]]
        :value (when (:ductile/id clean-value) clean-value)
        :on-select-item (fn [selected-item]
                          (on-change {:ductile/id (:ductile/id selected-item)
                                      :address/description (location-description selected-item)}))
        :on-clear #(on-change nil)
        :selected-item-view (fn [v] [:div.text-left.leading-snug [:span (:address/description v)]])
        :item-view item-view}])))

(defn locations-editor
  "Editor for a map-of {uuid description-string} representing multiple locations."
  [{:keys [value on-change editing? path diff-status]}]
  (let [value (or value {})]
    [:div.flex.flex-col.gap-2 {:class (when editing? "px-1")}
     (for [[id desc] value]
       (let [plain-id (strip-diff-records id)
             entry-status (or diff-status
                              (cond
                                (insertion? id) :inserted
                                (insertion? desc) :inserted
                                (deletion? id) :deleted
                                (deletion? desc) :deleted
                                :else nil))]
         ^{:key (str plain-id)}
         [:div.flex.items-center.gap-2.text-xs.justify-between
          {:class (case entry-status
                    :inserted "text-green-700 font-medium"
                    :deleted "text-red-500 font-medium line-through hover:no-underline opacity-70"
                    nil)}
          [location-editor {:value {:address/description
                                    (if entry-status (strip-diff-records desc) desc)}
                            :editing? false}]
          (when (and editing? (not= entry-status :deleted))
            [ui/link {:class "text-red-500 hover:text-red-700 text-[11px]"
                      :on-click #(on-change (dissoc value plain-id))}
             (tr "Delete")])]))
     (when editing?
       [location-editor {:editing? true
                         :path (conj (or path []) :locations-add)
                         :value nil
                         :on-change (fn [{:keys [ductile/id address/description]}]
                                      (when id
                                        (on-change (assoc value id description))))}])]))

(defn editor' [{:as opts :keys [value on-change schema schema-properties path editable-paths editing-path attr collapsed-by-default? label editing? diff-status]}]
  (let [schema (if (keyword? schema)
                 (or (m/deref (m/schema schema)) schema)
                 schema)
        schema-type (m/type schema)
        schema-properties (merge (m/properties schema) schema-properties)
        ;; Use canonical view from schema-type-defs, or fall back to schema type
        editor-view (or (:editor/view schema-properties)
                        (form->type (m/form schema))
                        (:view (schema-type-defs schema-type))
                        schema-type)
        path (or path [])
        label (or label (when attr (tr attr)))
        editing? (or editing? (and editing-path (= (take (count editing-path) path) editing-path)))
        structural-diff-value (when (and (diff-record? value)
                                         (contains? #{:cat :or} editor-view))
                                value)

        ;; Structural unwrapping: if value is a diff record wrapping a structural type,
        ;; unwrap and pass diff-status so children can render borders/backgrounds
        [value diff-status] (if (and (diff-record? value)
                                     (contains? structural-views editor-view))
                              (diff-unwrap value)
                              [value diff-status])

        ;; Collapse map diffs for non-structural views (e.g. :map with :editor/view :enum)
        ;; so the value is treated as atomic for diff display
        value (if (and (map? value)
                       (not (diff-record? value))
                       (not (contains? structural-views editor-view))
                       (some diff-record? (vals value)))
                (collapse-diff-map value)
                value)

        ;; Suppress editing for deleted structures
        editing? (if (= diff-status :deleted) false editing?)

        ;; For editing mode: unwrap simple diff records to get the current value
        ;; Can't use (or) because unwrapped value may be nil (e.g. Insertion(nil))
        edit-value? (and editing? (diff-record? value) (not (deletion? value)))
        edit-value (when edit-value?
                     (first (diff-unwrap value)))

        ;; billing/amount is stored in cents but displayed/edited in euros
        cents? (= attr :billing/amount)
        editor-view (if cents? :double editor-view)
        cents->euros (fn [v] (if (number? v) (/ v 100.0) v))
        value (if cents?
                (let [v (if edit-value? edit-value value)]
                  (cond
                    (mismatch? v) (diff-impl/->Mismatch (cents->euros (:- v)) (cents->euros (:+ v)))
                    (insertion? v) (diff-impl/->Insertion (cents->euros (:+ v)))
                    (deletion? v) (diff-impl/->Deletion (cents->euros (:- v)))
                    :else (cents->euros v)))
                (if edit-value? edit-value value))
        on-change (if cents?
                    (fn [v] (on-change (when (number? v) (Math/round (* v 100)))))
                    on-change)

        opts (merge opts {:schema schema :schema-properties schema-properties :path path
                          :label label :editing? editing? :diff-status diff-status
                          :value value :on-change on-change
                          :structural-diff-value structural-diff-value})
        leaf-double-click-props (when (and (not editing?)
                                           (not (contains? structural-views editor-view)))
                                  (edit-on-double-click-props {:edit! (:edit! opts)
                                                               :editable-paths editable-paths
                                                               :editing? editing?
                                                               :editing-path editing-path
                                                               :schema schema
                                                               :value value
                                                               :path path
                                                               :editor-view editor-view
                                                               :hidden-paths (:hidden-paths opts)}))]
    [:div (merge {:data-path (pr-str path)
                  :tab-index -1
                  :class (when editing? (str edit-form-font-class " leading-tight"))
                  :on-double-click (when editing?
                                     (fn [e]
                                       (.stopPropagation e)))}
                 leaf-double-click-props)
     (case editor-view
       :location [location-editor opts]

       :locations [locations-editor opts]

       :cas-file [cas-files-editor opts]

       :map [map-editor opts]

       :list [list-editor (assoc opts :child-schema (first (m/children schema)))]

       :set (let [child-schema (first (m/children schema))
                  child-type (m/type child-schema)
                  enum-options (when (= child-type :enum) (m/children child-schema))]
              (if (and editing?
                       (= child-type :enum)
                       (< (count enum-options) 10)
                       (nil? (:editor/view schema-properties)))
                [set-checkbox-editor (merge opts {:value (set (if edit-value? edit-value value))
                                                  :on-change on-change
                                                  :collection-kind :set
                                                  :options enum-options})]
                [list-editor (merge opts {:value (vec (if edit-value? edit-value value))
                                          :on-change (fn [v] (on-change (set v)))
                                          :child-schema child-schema
                                          :allow-reorder? false
                                          :path path})]))

       :map-of [map-of-editor opts]

       :cat [cat-editor opts]

       :tuple (let [child-schemas (m/children schema)
                    value (vec (or (if edit-value? edit-value value) (map default-value child-schemas)))]
                [:ul.flex.flex-col.gap-1
                 (map-indexed
                  (fn [i child-schema]
                    (let [child-value (nth value i (default-value child-schema))
                          child-diff-status (or diff-status
                                                (cond (insertion? child-value) :inserted
                                                      (deletion? child-value) :deleted
                                                      :else nil))]
                      [:li {:key i}
                       [editor' (merge opts {:value child-value
                                             :on-change #(on-change (assoc value i %))
                                             :schema child-schema
                                             :schema-properties nil
                                             :diff-status child-diff-status
                                             :path (conj path i)})]]))
                  child-schemas)])

       :and (if-let [inner (structural-child schema)]
              [editor' (assoc opts :schema inner)]
              [:div.text-red-500 "No structural schema found in :and"])

       :multi [multi-editor opts]

       :or [or-editor opts]

       := (tr (or (:label schema-properties)
                  (first (m/children schema))))

       (if editing?
         (if (deletion? value)
           ;; Deleted value: show as readonly even in edit mode
           [simple-readonly-value (assoc opts :value value)]
           (case editor-view
             :enum [enum-editor (assoc opts
                                       :options (or (:editor/options schema-properties) (m/children schema))
                                       :label-key (:label-key schema-properties)
                                       :description-key (:description-key schema-properties))]

             :checkbox-set [set-checkbox-editor (merge opts {:value (if edit-value? edit-value value)
                                                             :on-change on-change
                                                             :collection-kind (if (= schema-type :set) :set :vector)
                                                             :options (m/children (first (m/children schema)))})]

             :re [string-editor opts]

             :keyword [string-editor (assoc opts :coerce :keyword)]

             :any [string-editor (merge opts
                                        {:value (pr-str (if edit-value? edit-value value))
                                         :on-change #(try (edn/read-string %) (catch :default _ %))})]

             :boolean [boolean-editor (assoc opts :attr attr)]

             :date [date-editor opts]

             :inst [datetime-editor opts]

             :time/year-month [year-month-editor opts]

             :uuid [code-editor (merge opts
                                       {:format-value pr-str
                                        :parse-value #(try (edn/read-string %) (catch :default _ %))})]

             :int [int-editor opts]

             :double [double-editor opts]

             :string [string-editor opts]
             :textarea [string-editor (assoc opts :lines 5)]

             :fn (let [v (if edit-value? edit-value value)]
                   [code-editor {:value (:form v)
                                 :schema-properties schema-properties
                                 :format-value pr-str
                                 :parse-value #(try (rec/map->WorkflowFn
                                                     {:form (edn/read-string %)})
                                                    (catch :default _ v))
                                 :on-change on-change}])

             zero? [:div.font-mono.text-right 0]
             (:nil nil?) nil

             (let [deref-schema (m/deref schema)]
               (if (not= deref-schema schema)
                 [editor' (assoc opts :schema deref-schema)]
                 [:div.text-red-500 (tr "missing view for: %1" editor-view)]))))

         (case editor-view
           (:set :checkbox-set)
           (let [items (cond
                         (mismatch? value)
                         (let [old-set (set (:- value))
                               new-set (set (:+ value))
                               removed (map #(diff-impl/->Deletion %) (remove new-set old-set))
                               added (map #(diff-impl/->Insertion %) (remove old-set new-set))
                               unchanged (filter new-set old-set)]
                           (concat unchanged added removed))

                         (insertion? value)
                         (map #(diff-impl/->Insertion %) (:+ value))

                         (deletion? value)
                         (map #(diff-impl/->Deletion %) (:- value))

                         (coll? value) value

                         :else [value])]
             [:div {:class "flex flex-wrap gap-x-2 gap-y-0.5"}
              (doall (for [item (sort-by #(str (first (diff-unwrap %))) items)]
                       (let [[inner-value _] (diff-unwrap item)]
                         [:div.text-xs.whitespace-nowrap
                          {:key (str inner-value)
                           :class (cond
                                    (insertion? item) "text-green-700 font-medium"
                                    (deletion? item) "text-red-500 font-medium line-through hover:no-underline opacity-70"
                                    (= diff-status :inserted) "text-green-700 font-medium"
                                    (= diff-status :deleted) "text-red-500 font-medium line-through hover:no-underline opacity-70")}
                          (tr inner-value)])))])
           :enum [simple-readonly-value (update-in opts [:schema-properties :label-key] #(or % tr))]
           :date [inst-readonly-value opts]
           [simple-readonly-value opts])))]))


(defn breadcrumb-bar [hovering-path]
  [:div.sticky.top-0.bg-white.border-b.border-slate-200.px-3.pt-2.text-xs.text-slate-500.flex.items-center.gap-1.cursor-default
   {:class ["pb-0.5" (when-not (seq hovering-path) "invisible")]
    :style {:z-index 2}}
   [breadcrumbs hovering-path]])

(def pre-edit-unset ::pre-edit-unset)

(defn multiline-editor-target?
  [^js target]
  (or (= "TEXTAREA" (some-> target .-tagName))
      (some-> target (.closest ".cm-editor"))
      (true? (.-isContentEditable target))))

(defn editor [{:as opts :keys [schema value on-change breadcrumbs editing-path editable-paths editing? on-hover-path on-click-path validate? on-editing-path-change revealed-demoted-paths]}]
  (let [!editing-path (hooks/use-state editing-path)
        !active-path (hooks/use-state nil)
        !hovering-path (hooks/use-state nil)
        !revealed-demoted-paths-internal (hooks/use-state #{})
        revealed-demoted-paths-all (into (or revealed-demoted-paths #{}) @!revealed-demoted-paths-internal)
        reveal-demoted! (fn [path] (swap! !revealed-demoted-paths-internal conj path))
        !pending-commit (hooks/use-ref nil)
        register-pending-commit! (fn [commit!] (set! (.-current !pending-commit) commit!))
        commit-pending! (fn []
                          (when-let [commit! (.-current !pending-commit)]
                            (commit!))
                          (set! (.-current !pending-commit) nil))
        !pre-edit-value (hooks/use-ref pre-edit-unset)
        !validation-value (hooks/use-state (when validate? value))
        editable-paths (or editable-paths #{[]})
        inline-editing? (and (not editing?) @!editing-path)
        visible-validation-issues (hooks/use-memo #(when validate? (validation-issues schema @!validation-value))
                                                  [validate? schema @!validation-value])
        edit! (if editing?
                identity
                (fn [edit-path]
                  (cond (and edit-path (not @!editing-path))
                        (do
                          (reset! !pre-edit-value value)
                          (when validate? (reset! !validation-value value)))
                        (nil? edit-path)
                        (do
                          (commit-pending!)
                          (reset! !pre-edit-value pre-edit-unset)))
                  (reset! !editing-path edit-path)
                  (when on-editing-path-change
                    (on-editing-path-change edit-path))))
        cancel! (fn []
                  (register-pending-commit! nil)
                  (when-not (= pre-edit-unset @!pre-edit-value)
                    (on-change @!pre-edit-value))
                  (reset! !pre-edit-value pre-edit-unset)
                  (reset! !editing-path nil)
                  (when on-editing-path-change
                    (on-editing-path-change nil)))
        dirty-edit? (fn []
                      (and @!editing-path
                           (not= pre-edit-unset @!pre-edit-value)
                           (not= value @!pre-edit-value)))
        save-and-exit! (fn []
                         (when-let [^js active-el js/document.activeElement]
                           (when (or (= "INPUT" (.-tagName active-el))
                                     (= "SELECT" (.-tagName active-el))
                                     (= "TEXTAREA" (.-tagName active-el))
                                     (true? (.-isContentEditable active-el))
                                     (.closest active-el ".cm-editor"))
                             (.blur active-el)))
                         (js/setTimeout #(edit! nil) 0))
        handle-key-down! (fn [^js e]
                           (when (and @!editing-path
                                      (not editing?)
                                      (not (.-defaultPrevented e))
                                      (not (.-altKey e)))
                             (case (.-key e)
                               "Enter"
                               (when (and (or (.-metaKey e) (.-ctrlKey e))
                                          (not (.-shiftKey e)))
                                 (.preventDefault e)
                                 (.stopPropagation e)
                                 (save-and-exit!))

                               "Escape"
                               (do
                                 (.preventDefault e)
                                 (.stopPropagation e)
                                 (when (or (not (dirty-edit?))
                                           (js/window.confirm (tr "Discard unsaved changes?")))
                                   (cancel!)))
                               nil)))
        hover! (fn [path]
                 (reset! !hovering-path path)
                 (when on-hover-path (on-hover-path path)))
        click! (fn [path]
                 (when on-click-path (on-click-path path)))
        select-validation-path! (fn [path]
                                  (let [exact-path-exists? (value-path-exists? value path)
                                        edit-path (if exact-path-exists?
                                                    path
                                                    (closest-existing-value-path value path))]
                                    (when (path-editable? editable-paths edit-path)
                                      (edit! edit-path))
                                    (click! path)
                                    (reset! !active-path path)
                                    (reveal-path! path :delay 80)
                                    (js/setTimeout #(reset! !active-path nil) 160)))]
    (hooks/use-effect
     (fn []
       (when (not= editing-path @!editing-path)
         (reset! !editing-path editing-path)))
     [editing-path])
    (hooks/use-effect
     (fn []
       (.addEventListener js/document "keydown" handle-key-down! true)
       #(.removeEventListener js/document "keydown" handle-key-down! true))
     [handle-key-down!])
    (hooks/use-effect
     (fn []
       (when (and validate? (not inline-editing?))
         (reset! !validation-value value)))
     [validate? inline-editing? value])
    (hooks/use-effect
     (fn []
       (when on-editing-path-change
         (on-editing-path-change @!editing-path)))
     [@!editing-path])
    [:div.schema-based-editor.flex.flex-col
     {:class (if editing? "gap-1" "gap-0.5")
      :on-mouse-leave #(do
                         (reset! !hovering-path nil)
                         (when on-hover-path (on-hover-path nil)))}
     #_[breadcrumb-bar (concat breadcrumbs @!hovering-path)]
     (when (seq visible-validation-issues)
       [validation-summary {:schema schema
                            :issues visible-validation-issues
                            :on-select-path select-validation-path!}])
     [editor' (assoc opts
                     :root-schema schema
                     :root-value value
                     :editing-path @!editing-path
                     :active-path @!active-path
                     :edit! edit!
                     :cancel! cancel!
                     :register-pending-commit! register-pending-commit!
                     :hovering-path @!hovering-path
                     :editable-paths editable-paths
                     :revealed-demoted-paths revealed-demoted-paths-all
                     :reveal-demoted! reveal-demoted!
                     :click! click!
                     :hover! hover!)]]))

(defn- demo [{:keys [testid schema default-value props]}]
  (let [value @(deframe/!route-get-in [::value testid] default-value)]
    [:div.border.bg-slate-50.rounded.shadow-sm {:data-testid testid}
     [:div.text-xs.font-mono.p-3
      [:span.font-semibold.mr-4 testid]
      [:span.text-slate-500 (pr-str schema)]]
     [:div.flex.gap-4.border-t
      [:div.p-3
       [editor (merge {:value value
                       :on-change #(deframe/route-assoc-in! [::value testid] %)
                       :editable-paths #{[]}
                       :schema schema}
                      props)]]
      [:div.border-l.p-3.text-xs.text-slate-500.whitespace-pre-wrap.font-mono.sticky.top-5.max-h-screen.overflow-auto
       {:data-testid (str testid "-value")}
       (pr-str value)]]]))

(defn view [a b state]
  [:div.flex.flex-col.gap-6.p-4.max-w-4xl

   [demo
    {:testid "string"
     :schema [:map
              [:name {:label "Name" :description "A simple string field"} :string+]]
     :default-value {}}]

   [demo
    {:testid "simple-always-editing"
     :schema (into [:map
                    [:name {:label "Name" :description "A simple string field"} :string+]]
                   (map (fn [schema-type]
                          [schema-type {:label (str "A " schema-type) :description "Supports description"} schema-type]))
                   (disj simple :enum :re :factor))
     :props {:editing? true}
     :default-value {}}]

   [demo
    {:testid "or-simple"
     :schema [:map
              [:value [:or :string :int :boolean [:enum 'symbol-a 'symbol-b]]]]
     :default-value {}}]

   [demo
    {:testid "cat-simple"
     :schema [:map
              [:cat {:label "Cat"} [:cat [:+ :string] [:? :boolean]]]]
     :default-value {:cat nil}}]

   [demo
    {:testid "cat-maps"
     :schema [:cat
              [:+ [:map
                   [:billing/description {:optional true} :string+]
                   [:billing/price :billing/price]
                   [:billing/preds [:vector [:or :rec/fn (into [:enum] ['hv-battery? 'eitting-fuel?])]]]]]
              [:? [:or :billing/price :nil]]]
     :default-value []}]])


(comment
  @(deframe/!route-get-in [:value])
  @(deframe/!get-in []))
